// overviewOld.js — the pre-Feb-2026 "Weekly Report" overview layout, restored
// from git (f658679^). Reads and writes the same 'Overview' tab rows as
// overview.js, which was forked from this file, so shared fields stay in sync.
import React, { useEffect, useState, useMemo } from "react";
import { useAtom } from "jotai";
import moment from "moment";
import * as XLSX from 'xlsx';
import { updateUserDataArray, fNum, pNum } from '../db/company';
import LaborGraphs from './laborGraphs';
import {
    jobDataAtom,
    jobUserDataAtom,
    jobOpenCommitmentsAtom,
    jobBillingsAtom,
    jobCostDetailAtom,
    jobCostDetailCondensedAtom,
    selectedJobAtom,
    selectedTeamAtom,
    selectedVersionAtom,
    dayZeroAtom,
    dataSourceVersionAtom,
    budgetDeltaWipAtom,
    laborDeltaWipAtom,
    equipmentDeltaWipAtom,
    materialsDeltaWipAtom,
    subDeltaWipAtom,
    otherDeltaWipAtom,
    overheadDeltaWipAtom,
    projectCompleteAtom,
    isLiveVersionAtom,
    selectedJobDataAtom,
    overheadDataAtom,
    contingencyDeltaWipAtom,
    selectedWIPAtom,
    selectedVersionLockedAtom,
    dataSourceJobsAtom,
    equipmentRatesAtom,
    loadingCostAtom,
    dataSourceJobDataAtom,
    k2DataAtom,
    jobUserDataPrevAtom,
    jobOpenCommitmentsPrevAtom,
    jobCostDetailPrevAtom,
    jobPhaseCodesAtom
} from '../store/company';



function Overview() {
    const [selectedJobDetails, setSelectedJobDetails] = useAtom(selectedJobDataAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [versionInfo, setVersionInfo] = useState({});
    const [lockedVersion, setLockedVersion] = useAtom(selectedVersionLockedAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailCondensedAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobUserDataPrev] = useAtom(jobUserDataPrevAtom);
    const [jobCostDetailPrev] = useAtom(jobCostDetailPrevAtom);
    const [jobOpenCommitmentsPrev] = useAtom(jobOpenCommitmentsPrevAtom);
    const [dayZero] = useAtom(dayZeroAtom);
    const [budgetDeltaWip] = useAtom(budgetDeltaWipAtom);
    const [k2Data] = useAtom(k2DataAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);

    const [projectStatusNarrative, setProjectStatusNarrative] = useState("");
    const [isEditingNarrative, setIsEditingNarrative] = useState(false);
    
    // Safety indicators state
    const [safetyData, setSafetyData] = useState({
        strike: { hours: 0, firstAid: 0, nearMiss: 0, ltas: 0, weatherImpacts: 0, incidentRates: 0, qaqc: 0 },
        subs: [] // Array of sub objects with id, name, and field values
    });
    const [editingSafetyCell, setEditingSafetyCell] = useState(null);
    const [editingSubId, setEditingSubId] = useState(null);
    const [editingSubName, setEditingSubName] = useState('');
    
    const [accomplishments, setAccomplishments] = useState("");
    const [isEditingAccomplishments, setIsEditingAccomplishments] = useState(false);
    const [plannedActivities, setPlannedActivities] = useState("");
    const [isEditingPlannedActivities, setIsEditingPlannedActivities] = useState(false);
    const [opportunities, setOpportunities] = useState("");
    const [isEditingOpportunities, setIsEditingOpportunities] = useState(false);
    const [additionalComments, setAdditionalComments] = useState("");
    const [isEditingAdditionalComments, setIsEditingAdditionalComments] = useState(false);
    
    // Critical path notes state
    const [criticalPathNotes, setCriticalPathNotes] = useState("");
    const [isEditingCriticalPathNotes, setIsEditingCriticalPathNotes] = useState(false);
    
    // Quality/Rework tracking state
    const [reworkData, setReworkData] = useState([]);
    const [editingReworkId, setEditingReworkId] = useState(null);
    const [editingRework, setEditingRework] = useState(null);
    
    // Quantity tracking state
    const [quantityData, setQuantityData] = useState({});
    const [editingQuantityCell, setEditingQuantityCell] = useState(null);
    
    // Manpower chart data from Phase Hours
    const [manpowerChartData, setManpowerChartData] = useState([]);
    const [cashOutVariances, setCashOutVariances] = useState("");
    const [isEditingCashOutVariances, setIsEditingCashOutVariances] = useState(false);
    const [trendsCash, setTrendsCash] = useState("");
    const [isEditingTrendsCash, setIsEditingTrendsCash] = useState(false);

    // Status dot popup state
    const [statusPopup, setStatusPopup] = useState(null); // { category, anchorRect }

    // Progress section editable state
    const [progressData, setProgressData] = useState({});
    const [editingProgressCell, setEditingProgressCell] = useState(null);
    const [mathPopup, setMathPopup] = useState(null); // { column, discipline, anchorRect }
    const [periodExpanded, setPeriodExpanded] = useState(false); // Period columns collapsed by default

    // Rework Hours box state (for Quality section)
    const [reworkHoursData, setReworkHoursData] = useState({});
    const [editingReworkHoursCell, setEditingReworkHoursCell] = useState(null);

    // Change order notes state (for Budget section)
    const [changeOrderNotes, setChangeOrderNotes] = useState('');
    const [isEditingChangeOrderNotes, setIsEditingChangeOrderNotes] = useState(false);

    // Billing notes state
    const [billingNotes, setBillingNotes] = useState('');
    const [isEditingBillingNotes, setIsEditingBillingNotes] = useState(false);

    // Risks state
    const [risks, setRisks] = useState([]);
    const [editingRiskId, setEditingRiskId] = useState(null);
    const [editingRisk, setEditingRisk] = useState(null);

    // Issues state
    const [issues, setIssues] = useState([]);
    const [editingIssueId, setEditingIssueId] = useState(null);
    const [editingIssue, setEditingIssue] = useState(null);
    
    // Milestones state
    const [milestones, setMilestones] = useState([]);
    const [editingMilestoneId, setEditingMilestoneId] = useState(null);
    const [editingMilestone, setEditingMilestone] = useState(null);

    // Budget calculation state
    const [budgetData, setBudgetData] = useState({});
    const [etcBreakdownExpanded, setEtcBreakdownExpanded] = useState(false);
    const [spiExpanded, setSpiExpanded] = useState(true); // SPI permanently expanded
    const [editingSpiKey, setEditingSpiKey] = useState(null);
    const [editedSpiValues, setEditedSpiValues] = useState({});
    const [editingPfKey, setEditingPfKey] = useState(null);
    const [editedPfValues, setEditedPfValues] = useState({});
    const [editingCpiKey, setEditingCpiKey] = useState(null);
    const [editedCpiValues, setEditedCpiValues] = useState({});
    const [budgetNotes, setBudgetNotes] = useState({});
    const [editingBudgetNote, setEditingBudgetNote] = useState(null);
    const [collapsedDisciplines, setCollapsedDisciplines] = useState({});
    const [disciplineTitles, setDisciplineTitles] = useState({});
    const [editingDisciplineTitle, setEditingDisciplineTitle] = useState(null);

    // Load saved data from jobUserData
    useEffect(() => {
        if (jobUserData && selectedJob && selectedVersion) {
            // Single pass through jobUserData
            const loadedRisks = [];
            const loadedIssues = [];
            const loadedMilestones = [];
            const loadedReworks = [];
            
            jobUserData.forEach(item => {
                if (item.Forecast_Tab === 'Overview' &&
                    item.Version_GUID === selectedVersion &&
                    item.BU_ID === selectedJob) {
                    console.log('Found overview item:', item);
                    
                    switch(item.Forecast_Field_Name) {
                        case 'ProjectStatusNarrative':
                            setProjectStatusNarrative(item.Forecast_Value);
                            break;
                        case 'Accomplishments':
                            setAccomplishments(item.Forecast_Value);
                            break;
                        case 'PlannedActivities':
                            setPlannedActivities(item.Forecast_Value);
                            break;
                        case 'Opportunities':
                            setOpportunities(item.Forecast_Value);
                            break;
                        case 'AdditionalComments':
                            setAdditionalComments(item.Forecast_Value);
                            break;
                        case 'CashOutVariances':
                            setCashOutVariances(item.Forecast_Value);
                            break;
                        case 'TrendsCash':
                            setTrendsCash(item.Forecast_Value);
                            break;
                        case 'BudgetNote':
                            // Load budget notes by discipline/cost code
                            setBudgetNotes(prev => ({
                                ...prev,
                                [item.Forecast_Row_ID]: item.Forecast_Value
                            }));
                            break;
                        case 'DisciplineTitle':
                            // Load discipline custom titles
                            setDisciplineTitles(prev => ({
                                ...prev,
                                [item.Forecast_Row_ID]: item.Forecast_Value
                            }));
                            break;
                        case 'safetyData':
                            try {
                                const parsed = JSON.parse(item.Forecast_Value || '{}');
                                // Merge with defaults to ensure all fields exist
                                const defaultStrike = { hours: 0, firstAid: 0, nearMiss: 0, ltas: 0, weatherImpacts: 0, incidentRates: 0, qaqc: 0 };
                                setSafetyData({
                                    strike: { ...defaultStrike, ...parsed.strike },
                                    subs: Array.isArray(parsed.subs) ? parsed.subs : []
                                });
                            } catch (e) {
                                console.error('Error parsing safety data:', e);
                            }
                            break;
                        case 'Risk':
                            try {
                                const risk = JSON.parse(item.Forecast_Value);
                                loadedRisks.push({ ...risk, id: item.Forecast_Row_ID });
                            } catch (e) {
                                console.error('Error parsing risk:', e);
                            }
                            break;
                        case 'Issue':
                            try {
                                const issue = JSON.parse(item.Forecast_Value);
                                loadedIssues.push({ ...issue, id: item.Forecast_Row_ID });
                            } catch (e) {
                                console.error('Error parsing issue:', e);
                            }
                            break;
                        case 'Milestone':
                            try {
                                const milestone = JSON.parse(item.Forecast_Value);
                                loadedMilestones.push({ ...milestone, id: item.Forecast_Row_ID });
                            } catch (e) {
                                console.error('Error parsing milestone:', e);
                            }
                            break;
                        case 'ProgressWeight':
                        case 'ProgressPeriodPlan':
                        case 'ProgressPeriodActual':
                        case 'ProgressPeriodFcast':
                        case 'ProgressPeriodDeltaPercent':
                        case 'ProgressPeriodDeltaDollar':
                        case 'ProgressCumulativePlan':
                        case 'ProgressCumulativeActual':
                        case 'ProgressCumulativeFcast':
                        case 'ProgressCumulativeTrending':
                        case 'ProgressCumulativeDeltaPlan':
                            // Load progress data by discipline
                            setProgressData(prev => ({
                                ...prev,
                                [`${item.Forecast_Row_ID}_${item.Forecast_Field_Name}`]: item.Forecast_Value
                            }));
                            break;
                        case 'CriticalPathNotes':
                            setCriticalPathNotes(item.Forecast_Value);
                            break;
                        case 'Rework':
                            try {
                                const rework = JSON.parse(item.Forecast_Value);
                                loadedReworks.push({ ...rework, id: item.Forecast_Row_ID });
                            } catch (e) {
                                console.error('Error parsing rework:', e);
                            }
                            break;
                        case 'QuantityPlanned':
                        case 'QuantityActual':
                        case 'QuantityUnit':
                            setQuantityData(prev => ({
                                ...prev,
                                [`${item.Forecast_Row_ID}_${item.Forecast_Field_Name}`]: item.Forecast_Value
                            }));
                            break;
                        case 'ReworkHours':
                            setReworkHoursData(prev => ({
                                ...prev,
                                [item.Forecast_Row_ID]: item.Forecast_Value
                            }));
                            break;
                        case 'ChangeOrderNotes':
                            setChangeOrderNotes(item.Forecast_Value);
                            break;
                        case 'BillingNotes':
                            setBillingNotes(item.Forecast_Value);
                            break;
                    }
                }
            });
            
            if (loadedRisks.length > 0) setRisks(loadedRisks);
            if (loadedIssues.length > 0) setIssues(loadedIssues);
            if (loadedMilestones.length > 0) setMilestones(loadedMilestones);
            if (loadedMilestones.length > 0) setMilestones(loadedMilestones);
            if (loadedReworks.length > 0) setReworkData(loadedReworks);
        }
    }, [jobUserData, selectedJob, selectedVersion]);

    const handleSaveNarrative = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'ProjectStatusNarrative',
            value: projectStatusNarrative,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingNarrative(false);
    };

    const handleSaveAccomplishments = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'Accomplishments',
            value: accomplishments,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingAccomplishments(false);
    };

    const handleSavePlannedActivities = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'PlannedActivities',
            value: plannedActivities,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingPlannedActivities(false);
    };

    const handleSaveOpportunities = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'Opportunities',
            value: opportunities,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingOpportunities(false);
    };

    const handleSaveAdditionalComments = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'AdditionalComments',
            value: additionalComments,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingAdditionalComments(false);
    };


    const handleSaveCashOutVariances = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'CashOutVariances',
            value: cashOutVariances,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingCashOutVariances(false);
    };

    const handleSaveTrendsCash = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'TrendsCash',
            value: trendsCash,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingTrendsCash(false);
    };

    // Budget note handler
    const handleSaveBudgetNote = (rowId, value) => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'BudgetNote',
            value: value,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: rowId,
            lockedVersion: lockedVersion
        });
        setBudgetNotes(prev => ({ ...prev, [rowId]: value }));
        setEditingBudgetNote(null);
    };

    // Discipline title handler
    const handleSaveDisciplineTitle = (discipline, value) => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'DisciplineTitle',
            value: value,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: discipline,
            lockedVersion: lockedVersion
        });
        setDisciplineTitles(prev => ({ ...prev, [discipline]: value }));
        setEditingDisciplineTitle(null);
    };

    // Rework hours handler (for Quality section box)
    const handleReworkHoursChange = (discipline, value) => {
        setReworkHoursData(prev => ({ ...prev, [discipline]: value }));
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'ReworkHours',
            value: value,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: discipline,
            lockedVersion: lockedVersion
        });
        setEditingReworkHoursCell(null);
    };

    // Change order notes handler
    const handleSaveChangeOrderNotes = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'ChangeOrderNotes',
            value: changeOrderNotes,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingChangeOrderNotes(false);
    };

    // Billing notes handler
    const handleSaveBillingNotes = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'BillingNotes',
            value: billingNotes,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingBillingNotes(false);
    };

    // Status dot popup handler
    const handleStatusPopup = (e, category) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        if (statusPopup && statusPopup.category === category) {
            setStatusPopup(null);
        } else {
            setStatusPopup({ category, anchorRect: { top: rect.bottom, left: rect.left } });
        }
    };

    // Render status dot popup
    const renderStatusPopup = () => {
        if (!statusPopup) return null;
        const formulas = {
            overall: { label: 'Overall Status', formula: 'Worst of all individual status indicators', source: 'Composite of Productivity, Cost, Risk, Schedule, Billing, Safety', detail: 'Red if any indicator is Red; Orange if any is Orange; Green otherwise.' },
            productivity: { label: 'Productivity', formula: 'Based on Performance Factor (PF)', source: 'PF = EAC / Current Budget', detail: `PF Score: ${pfScore.toFixed(2)}\nRed: PF < 0.85\nOrange: PF 0.85–0.95\nGreen: PF ≥ 0.95` },
            cost: { label: 'Cost / Budget', formula: 'Based on Budget GM% variance', source: 'Job Details Budget field', detail: `Budget: ${selectedJobDetails?.Budget || 'N/A'}\nRed: GM% > 10% off budget\nOrange: GM% 3–10% off budget\nGreen: GM% < 3% off budget` },
            risk: { label: 'Risk', formula: 'Based on top risk severity', source: 'Risk register', detail: 'Currently using placeholder logic.\nGreen: No significant risks identified.' },
            schedule: { label: 'Schedule', formula: 'Based on days until planned completion', source: 'Planned Complete Date from Job Details', detail: `Planned Complete: ${selectedJobDetails?.Planned_Complete_Date ? moment(selectedJobDetails.Planned_Complete_Date).format('DD-MMM-YY') : 'N/A'}\nDays remaining: ${selectedJobDetails?.Planned_Complete_Date ? moment(selectedJobDetails.Planned_Complete_Date).diff(newDate, 'days') : 'N/A'}\nRed: Past due & not complete\nOrange: < 30 days remaining\nGreen: On schedule` },
            billing: { label: 'Billing', formula: 'Based on Over/Underbilled %', source: 'Over/Underbilled ratio', detail: `Over/Under: ${(overUnder * 100).toFixed(1)}%\nRed: ≥ 15% underbilled\nOrange: 5–15% underbilled\nGreen: < 5% underbilled or overbilled` },
            safety: { label: 'Safety', formula: 'Based on safety incidents', source: 'Safety tracking data', detail: 'Currently using placeholder logic.\nGreen: No safety incidents reported.' }
        };
        const info = formulas[statusPopup.category];
        if (!info) return null;
        return (
            <>
                <div onClick={() => setStatusPopup(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }} />
                <div style={{
                    position: 'fixed',
                    top: statusPopup.anchorRect.top + 4,
                    left: Math.min(statusPopup.anchorRect.left, window.innerWidth - 380),
                    zIndex: 9999,
                    backgroundColor: '#1a1a2e',
                    color: '#e0e0e0',
                    border: '1px solid #2d6cdf',
                    borderRadius: 8,
                    padding: '14px 18px',
                    minWidth: 320,
                    maxWidth: 370,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    lineHeight: 1.6,
                }}>
                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                        {info.label}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: '#aaa' }}>Formula: </span>
                        <span style={{ color: '#f5c842', whiteSpace: 'pre-wrap' }}>{info.formula}</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: '#aaa' }}>Source: </span>
                        <span style={{ color: '#82e0aa' }}>{info.source}</span>
                    </div>
                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', whiteSpace: 'pre-wrap', color: '#d5d8dc', fontSize: 11 }}>
                        {info.detail}
                    </div>
                    <div onClick={() => setStatusPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>
                        ✕ Close
                    </div>
                </div>
            </>
        );
    };

    // Progress section handlers
    const handleProgressCellChange = async (discipline, fieldName, value) => {
        const key = `${discipline}_${fieldName}`;
        setProgressData(prev => ({ ...prev, [key]: value }));
        
        console.log('Saving progress data:', { discipline, fieldName, value, selectedVersion, selectedJob });
        
        await updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: fieldName,
            value: value,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: discipline,
            lockedVersion: lockedVersion
        });
        
        setEditingProgressCell(null);
    };

    const getProgressValue = (discipline, fieldName, defaultValue = '0.0%') => {
        const key = `${discipline}_${fieldName}`;
        return progressData[key] || defaultValue;
    };

    // SPI editing handlers
    const handleSpiEdit = (dataKey, currentValue) => {
        setEditingSpiKey(dataKey);
        setEditedSpiValues({ ...editedSpiValues, [dataKey]: currentValue || '' });
    };

    const handleSpiSave = async (dataKey) => {
        const newValue = editedSpiValues[dataKey];
        if (newValue !== undefined && newValue !== '') {
            await updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'spi',
                value: newValue,
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: dataKey,
                lockedVersion: lockedVersion
            });
        }
        setEditingSpiKey(null);
    };

    const handleSpiChange = (dataKey, value) => {
        setEditedSpiValues({ ...editedSpiValues, [dataKey]: value });
    };

    const handlePfEdit = (dataKey, currentValue) => {
        setEditingPfKey(dataKey);
        setEditedPfValues({ ...editedPfValues, [dataKey]: currentValue || '' });
    };

    const handlePfSave = async (dataKey) => {
        const newValue = editedPfValues[dataKey];
        if (newValue !== undefined && newValue !== '') {
            await updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'pf',
                value: newValue,
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: dataKey,
                lockedVersion: lockedVersion
            });
        }
        setEditingPfKey(null);
    };

    const handlePfChange = (dataKey, value) => {
        setEditedPfValues({ ...editedPfValues, [dataKey]: value });
    };

    const handleCpiEdit = (dataKey, currentValue) => {
        setEditingCpiKey(dataKey);
        setEditedCpiValues({ ...editedCpiValues, [dataKey]: currentValue || '' });
    };

    const handleCpiSave = async (dataKey) => {
        const newValue = editedCpiValues[dataKey];
        if (newValue !== undefined && newValue !== '') {
            await updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'cpi',
                value: newValue,
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: dataKey,
                lockedVersion: lockedVersion
            });
        }
        setEditingCpiKey(null);
    };

    const handleCpiChange = (dataKey, value) => {
        setEditedCpiValues({ ...editedCpiValues, [dataKey]: value });
    };

    // Safety handlers
    const handleSafetyCellChange = async (crew, field, value) => {
        const numValue = parseFloat(value) || 0;
        const newSafetyData = {
            ...safetyData,
            [crew]: {
                ...safetyData[crew],
                [field]: numValue
            }
        };
        setSafetyData(newSafetyData);
        
        // Save to database
        await updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'safetyData',
            value: JSON.stringify(newSafetyData),
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        
        setEditingSafetyCell(null);
    };
    
    const getSafetyTotal = (field) => {
        const strikeValue = safetyData.strike[field] || 0;
        const subsTotal = safetyData.subs.reduce((sum, sub) => sum + (sub[field] || 0), 0);
        return strikeValue + subsTotal;
    };

    // Sub safety row handlers
    const handleAddSub = () => {
        const newSub = {
            id: Date.now().toString(),
            name: '',
            hours: 0,
            firstAid: 0,
            nearMiss: 0,
            ltas: 0,
            weatherImpacts: 0,
            incidentRates: 0,
            qaqc: 0
        };
        const newSafetyData = {
            ...safetyData,
            subs: [...safetyData.subs, newSub]
        };
        setSafetyData(newSafetyData);
        setEditingSubId(newSub.id);
        setEditingSubName('');
    };

    const handleSaveSubName = async (subId, newName) => {
        if (!newName.trim()) {
            // If name is empty, remove the sub
            const newSafetyData = {
                ...safetyData,
                subs: safetyData.subs.filter(s => s.id !== subId)
            };
            setSafetyData(newSafetyData);
        } else {
            const newSafetyData = {
                ...safetyData,
                subs: safetyData.subs.map(s => s.id === subId ? { ...s, name: newName } : s)
            };
            setSafetyData(newSafetyData);
            await updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'safetyData',
                value: JSON.stringify(newSafetyData),
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: '1',
                lockedVersion: lockedVersion
            });
        }
        setEditingSubId(null);
        setEditingSubName('');
    };

    const handleDeleteSub = async (subId) => {
        if (window.confirm('Are you sure you want to delete this sub row?')) {
            const newSafetyData = {
                ...safetyData,
                subs: safetyData.subs.filter(s => s.id !== subId)
            };
            setSafetyData(newSafetyData);
            await updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'safetyData',
                value: JSON.stringify(newSafetyData),
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: '1',
                lockedVersion: lockedVersion
            });
        }
    };

    const handleSubCellChange = async (subId, field, value) => {
        const numValue = parseFloat(value) || 0;
        const newSafetyData = {
            ...safetyData,
            subs: safetyData.subs.map(s => s.id === subId ? { ...s, [field]: numValue } : s)
        };
        setSafetyData(newSafetyData);
        
        await updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'safetyData',
            value: JSON.stringify(newSafetyData),
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
    };

    // Critical Path Notes handler
    const handleSaveCriticalPathNotes = () => {
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'CriticalPathNotes',
            value: criticalPathNotes,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: '1',
            lockedVersion: lockedVersion
        });
        setIsEditingCriticalPathNotes(false);
    };

    // Rework/Quality handlers
    const handleAddRework = () => {
        const newRework = {
            id: Date.now().toString(),
            discipline: '',
            description: '',
            hoursSpent: '',
            costImpact: '',
            rootCause: '',
            status: 'Open'
        };
        setEditingReworkId(newRework.id);
        setEditingRework(newRework);
        setReworkData([...reworkData, newRework]);
    };

    const handleEditRework = (rework) => {
        setEditingReworkId(rework.id);
        setEditingRework({ ...rework });
    };

    const handleSaveRework = () => {
        const updatedReworks = reworkData.map(r => r.id === editingReworkId ? editingRework : r);
        setReworkData(updatedReworks);
        
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'Rework',
            value: JSON.stringify(editingRework),
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: editingReworkId,
            lockedVersion: lockedVersion
        });
        setEditingReworkId(null);
        setEditingRework(null);
    };

    const handleCancelEditRework = () => {
        if (editingRework && !editingRework.discipline && !editingRework.description) {
            setReworkData(reworkData.filter(r => r.id !== editingReworkId));
        }
        setEditingReworkId(null);
        setEditingRework(null);
    };

    const handleDeleteRework = (reworkId) => {
        if (window.confirm('Delete this rework entry?')) {
            setReworkData(reworkData.filter(r => r.id !== reworkId));
            updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'Rework',
                value: '',
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: reworkId,
                lockedVersion: lockedVersion
            });
        }
    };

    // Quantity tracking handlers
    const handleQuantityCellChange = async (discipline, field, value) => {
        const key = `${discipline}_${field}`;
        setQuantityData(prev => ({ ...prev, [key]: value }));
        await updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: field,
            value: value,
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: discipline,
            lockedVersion: lockedVersion
        });
        setEditingQuantityCell(null);
    };

    const getQuantityValue = (discipline, field) => {
        return quantityData[`${discipline}_${field}`] || '';
    };

    // Risk handlers
    const handleAddRisk = () => {
        const newRisk = {
            id: Date.now().toString(),
            risk: '',
            description: '',
            actionTaken: '',
            impact: '',
            probability: ''
        };
        setEditingRiskId(newRisk.id);
        setEditingRisk(newRisk);
        setRisks([...risks, newRisk]);
    };

    const handleEditRisk = (risk) => {
        setEditingRiskId(risk.id);
        setEditingRisk({ ...risk });
    };

    const handleSaveRisk = () => {
        const updatedRisks = risks.map(r => r.id === editingRiskId ? editingRisk : r);
        setRisks(updatedRisks);
        
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'Risk',
            value: JSON.stringify(editingRisk),
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: editingRiskId,
            lockedVersion: lockedVersion
        });
        
        setEditingRiskId(null);
        setEditingRisk(null);
    };

    const handleCancelRisk = () => {
        if (!risks.find(r => r.id === editingRiskId)?.risk) {
            setRisks(risks.filter(r => r.id !== editingRiskId));
        }
        setEditingRiskId(null);
        setEditingRisk(null);
    };

    const handleDeleteRisk = (id) => {
        if (window.confirm('Are you sure you want to delete this risk?')) {
            setRisks(risks.filter(r => r.id !== id));
            // You may want to add a delete API call here
        }
    };

    // Issue handlers
    const handleAddIssue = () => {
        const newIssue = {
            id: Date.now().toString(),
            issue: '',
            description: '',
            actionTaken: '',
            priority: '',
            dateForResolution: ''
        };
        setEditingIssueId(newIssue.id);
        setEditingIssue(newIssue);
        setIssues([...issues, newIssue]);
    };

    const handleEditIssue = (issue) => {
        setEditingIssueId(issue.id);
        setEditingIssue({ ...issue });
    };

    const handleSaveIssue = () => {
        const updatedIssues = issues.map(i => i.id === editingIssueId ? editingIssue : i);
        setIssues(updatedIssues);
        
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'Issue',
            value: JSON.stringify(editingIssue),
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: editingIssueId,
            lockedVersion: lockedVersion
        });
        
        setEditingIssueId(null);
        setEditingIssue(null);
    };

    const handleCancelIssue = () => {
        if (!issues.find(i => i.id === editingIssueId)?.issue) {
            setIssues(issues.filter(i => i.id !== editingIssueId));
        }
        setEditingIssueId(null);
        setEditingIssue(null);
    };

    const handleDeleteIssue = (id) => {
        if (window.confirm('Are you sure you want to delete this issue?')) {
            setIssues(issues.filter(i => i.id !== id));
            // You may want to add a delete API call here
        }
    };

    // Milestone handlers
    const handleAddMilestone = () => {
        const newMilestone = {
            id: Date.now().toString(),
            milestone: '',
            owner: '',
            currentBaseline: '',
            actualForecast: '',
            prevForecast: '',
            remarks: '',
            weeklyDelta: '',
            baselineDelta: ''
        };
        setMilestones([...milestones, newMilestone]);
        setEditingMilestoneId(newMilestone.id);
        setEditingMilestone(newMilestone);
    };

    const handleEditMilestone = (milestone) => {
        setEditingMilestoneId(milestone.id);
        setEditingMilestone({ ...milestone });
    };

    const handleSaveMilestone = () => {
        if (!editingMilestone) return;

        // Calculate Weekly Delta: Actual/Forecast - Prev F/C (in days)
        let weeklyDelta = '';
        if (editingMilestone.actualForecast && editingMilestone.prevForecast) {
            const actualDate = moment(editingMilestone.actualForecast);
            const prevDate = moment(editingMilestone.prevForecast);
            weeklyDelta = actualDate.diff(prevDate, 'days');
        }

        // Calculate Baseline Delta: Current Baseline - Actual/Forecast (in days)
        let baselineDelta = '';
        if (editingMilestone.currentBaseline && editingMilestone.actualForecast) {
            const baselineDate = moment(editingMilestone.currentBaseline);
            const actualDate = moment(editingMilestone.actualForecast);
            baselineDelta = baselineDate.diff(actualDate, 'days');
        }

        const milestoneToSave = {
            ...editingMilestone,
            weeklyDelta,
            baselineDelta
        };

        const updatedMilestones = milestones.map((m) =>
            m.id === milestoneToSave.id ? milestoneToSave : m
        );
        setMilestones(updatedMilestones);

        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            tab: 'Overview',
            fieldName: 'Milestone',
            value: JSON.stringify(milestoneToSave),
            version: selectedVersion || '',
            id: selectedJob || '',
            rowId: milestoneToSave.id,
            lockedVersion: lockedVersion,
        });

        setEditingMilestoneId(null);
        setEditingMilestone(null);
    };

    const handleCancelEditMilestone = () => {
        if (editingMilestone && !editingMilestone.milestone) {
            setMilestones(milestones.filter((m) => m.id !== editingMilestone.id));
        }
        setEditingMilestoneId(null);
        setEditingMilestone(null);
    };

    const handleDeleteMilestone = (milestoneId) => {
        if (window.confirm('Are you sure you want to delete this milestone?')) {
            setMilestones(milestones.filter((m) => m.id !== milestoneId));
            updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                tab: 'Overview',
                fieldName: 'Milestone',
                value: '',
                version: selectedVersion || '',
                id: selectedJob || '',
                rowId: milestoneId,
                lockedVersion: lockedVersion,
                delete: true,
            });
        }
    };

    // Placeholder values - replace with actual data
    const newDate = new Date();
    let pfScore = 0.86;
    let overUnder = 0.12;

    // Status indicator functions
    const getBillingStatus = () => {
        if (overUnder >= 0.15) {
            return <span title="Red: Project is greater than 15% underbilled" style={{ color: '#d32f2f', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else if (overUnder >= 0.05 && overUnder < 0.15) {
            return <span title="Orange: Project is between 5% and 15% underbilled" style={{ color: '#f57c00', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else {
            return <span title="Green: Project is either overbilled or less than 5% underbilled" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
        }
    }; const getBudgetStatus = () => {
        if (selectedJobDetails?.Budget < 0.85) {
            return <span title="Red: Project GM% is greater than 10% off budget" style={{ color: '#d32f2f', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else if (pfScore > 0.85 && pfScore < 0.95) {
            return <span title="Orange: Project GM% is between 3% and 10% off budget" style={{ color: '#f57c00', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else {
            return <span title="Green: Project GM% is less than 3% off budget" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
        }
    }; const getProductivityStatus = () => {
        if (pfScore < 0.85) {
            return <span title="Red: Performance Factor is less than 0.85" style={{ color: '#d32f2f', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else if (pfScore >= 0.85 && pfScore < 0.95) {
            return <span title="Orange: Performance Factor is between 0.85 and 0.95" style={{ color: '#f57c00', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else {
            return <span title="Green: Performance Factor is greater than 0.95" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
        }
    }; const getScheduleStatus = () => {
        if (!selectedJobDetails?.Planned_Complete_Date) return <span title="Green: Project is on schedule to be complete" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;

        const daysUntilComplete = moment(selectedJobDetails.Planned_Complete_Date).diff(newDate, 'days');

        if (daysUntilComplete < 0 && selectedJobDetails.Job_Status !== 'C') {
            return <span title="Red: Project end date has passed and the job is not complete" style={{ color: '#d32f2f', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else if (daysUntilComplete < 30 && daysUntilComplete > 0 && selectedJobDetails.Job_Status !== 'C') {
            return <span title="Orange: Project end date is within 30 days and the job is not on schedule to be complete" style={{ color: '#f57c00', fontSize: '24px', cursor: 'help' }}>●</span>;
        } else {
            return <span title="Green: Project is on schedule to be complete" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
        }
    };

    const getRiskStatus = () => {
        // Placeholder - implement risk calculation logic
        return <span title="Green: No significant risks identified" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
    };

    const getSafetyStatus = () => {
        // Placeholder - implement safety calculation logic
        return <span title="Green: No safety incidents reported" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
    };

    const getOverallStatus = () => {
        const statuses = [
            getBillingStatus(),
            getBudgetStatus(),
            getProductivityStatus(),
            getScheduleStatus(),
            getRiskStatus(),
            getSafetyStatus()
        ];

        // Check if any status is red
        const hasRed = statuses.some(s => s?.props?.style?.color === '#d32f2f');
        const hasOrange = statuses.some(s => s?.props?.style?.color === '#f57c00');

        if (hasRed) return <span title="Red: One or more critical issues detected (Billing: 15%, Budget: 3%, Productivity: 40%, Schedule: 15%)" style={{ color: '#d32f2f', fontSize: '24px', cursor: 'help' }}>●</span>;
        if (hasOrange) return <span title="Orange: One or more warning conditions present" style={{ color: '#f57c00', fontSize: '24px', cursor: 'help' }}>●</span>;
        return <span title="Green: All project health indicators are within acceptable ranges" style={{ color: '#388e3c', fontSize: '24px', cursor: 'help' }}>●</span>;
    };

    const containerStyle = {
        fontFamily: "Arial, sans-serif",
        fontSize: 11,
        color: "#000",
        lineHeight: 1.3,
        border: "1px solid #999",
        width: "100%",
        maxWidth: 1300,
        margin: "0 auto",
        backgroundColor: "#fff",
    };

    const sectionHeaderStyle = {
        backgroundColor: "#e6edf5",
        fontWeight: "bold",
        padding: "4px 6px",
        borderBottom: "1px solid #999",
        borderTop: "1px solid #999",
    };

    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
    };

    const thStyle = {
        border: "1px solid #999",
        padding: "3px 4px",
        backgroundColor: "#f3f3f3",
        textAlign: "center",
        fontWeight: "bold",
    };

    const tdStyle = {
        border: "1px solid #999",
        padding: "3px 4px",
        textAlign: "left",
        verticalAlign: "top",
    };

    const tdStyleHeader = {
        border: "1px solid #999",
        padding: "3px 4px",
        textAlign: "left",
        verticalAlign: "top",
    };

    const centeredCell = {
        ...tdStyle,
        textAlign: "center",
    };

    // === Progress & Schedule column formula descriptions ===
    const progressColumnFormulas = {
        periodPlan: {
            label: 'Period Plan',
            formula: 'Phase Hours Planned (this week) ÷ Total Budget Hours × 100',
            source: 'Phase Hours tab (Labor Graphs)',
            detail: (disc, data) => {
                const ph = data || {};
                return `Period Planned Hrs: ${fNum(ph.periodPlan || 0)}\nTotal Budget Hrs: ${fNum(ph.totalBudgetHours || 0)}\nResult: ${ph.periodPlanPct || '0.0%'}`;
            }
        },
        periodActual: {
            label: 'Period Actual',
            formula: 'Manually entered or imported',
            source: 'User override',
            detail: () => 'This value is manually entered by the user.\nIt represents actual progress earned this period.'
        },
        periodFcast: {
            label: 'Period Forecast',
            formula: 'Manually entered or imported',
            source: 'User override',
            detail: () => 'This value is manually entered by the user.\nIt represents forecasted progress for this period.'
        },
        periodDeltaPercent: {
            label: 'Period Δ %',
            formula: 'Period Actual − Period Plan',
            source: 'Calculated or user override',
            detail: () => 'Variance between actual and planned progress for the period.\nPositive = ahead of plan, Negative = behind plan.'
        },
        periodDeltaDollar: {
            label: 'Period Δ $',
            formula: 'Period Δ % × Budget Hours × Blended Rate',
            source: 'Calculated or user override',
            detail: () => 'Dollar impact of the period variance.\nConverts the % delta into a cost impact.'
        },
        cumulativePlan: {
            label: 'Cumulative Plan',
            formula: 'Sum of Phase Hours (all weeks through this period) ÷ Total Budget Hours × 100',
            source: 'Phase Hours tab (Labor Graphs)',
            detail: (disc, data) => {
                const ph = data || {};
                return `Cumulative Planned Hrs: ${fNum(ph.cumulativePlan || 0)}\nTotal Budget Hrs: ${fNum(ph.totalBudgetHours || 0)}\nResult: ${ph.cumulativePlanPct || '0.0%'}`;
            }
        },
        cumulativeActual: {
            label: 'Cumulative Actual',
            formula: 'Total Actual Hours ÷ Budget Hours × 100',
            source: 'Cost Detail (JDE) + Day Zero Budget + Change Orders',
            detail: (disc, calcData) => {
                const d = calcData || {};
                return `Actual Hours: ${fNum(d.actualHours || 0)}\nBudget Hours: ${fNum(d.budgetHours || 0)}\nResult: ${d.actualPercent || '0.0%'}`;
            }
        },
        cumulativeFcast: {
            label: 'Cumulative Forecast',
            formula: '(Forecasted ETC Hours + Actual Hours) ÷ Budget Hours × 100',
            source: 'Productivity Tracker (forecastedHrs) + Cost Detail',
            detail: (disc, calcData) => {
                const d = calcData || {};
                return `PM EAC (Fcast + Actual): ${fNum(d.forecastHours || 0)}\nBudget Hours: ${fNum(d.budgetHours || 0)}\nResult: ${d.forecastPercent || '0.0%'}`;
            }
        },
        cumulativeTrending: {
            label: 'Cumulative Trending',
            formula: 'If % complete > 25%: (Actual Hrs/Qty × Total Qty) ÷ Budget Hrs × 100\nElse: max(Actual Hrs, Budget Hrs) ÷ Budget Hrs × 100',
            source: 'Scorecard (installed qty) + Cost Detail + Day Zero',
            detail: (disc, calcData) => {
                const d = calcData || {};
                return `Trending EAC Hrs: ${fNum(d.trendingHours || 0)}\nBudget Hours: ${fNum(d.budgetHours || 0)}\nResult: ${d.trendingPercent || '0.0%'}`;
            }
        },
        cumulativeDeltaPlan: {
            label: 'Cumulative Δ % Plan',
            formula: '(Actual Hours − Budget Hours) ÷ Budget Hours × 100',
            source: 'Cost Detail + Day Zero Budget + Change Orders',
            detail: (disc, calcData) => {
                const d = calcData || {};
                return `Actual Hours: ${fNum(d.actualHours || 0)}\nBudget Hours: ${fNum(d.budgetHours || 0)}\nDelta: ${d.deltaPlan || '0.0%'}`;
            }
        }
    };

    const handleMathPopup = (e, column, discipline, calcData) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        if (mathPopup && mathPopup.column === column && mathPopup.discipline === discipline) {
            setMathPopup(null);
        } else {
            setMathPopup({ column, discipline, calcData, anchorRect: { top: rect.bottom, left: rect.left } });
        }
    };

    const renderMathPopup = () => {
        if (!mathPopup) return null;
        const formula = progressColumnFormulas[mathPopup.column];
        if (!formula) return null;
        const detailText = formula.detail ? formula.detail(mathPopup.discipline, mathPopup.calcData) : '';
        return (
            <>
                <div onClick={() => setMathPopup(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }} />
                <div style={{
                    position: 'fixed',
                    top: mathPopup.anchorRect.top + 4,
                    left: Math.min(mathPopup.anchorRect.left, window.innerWidth - 380),
                    zIndex: 9999,
                    backgroundColor: '#1a1a2e',
                    color: '#e0e0e0',
                    border: '1px solid #2d6cdf',
                    borderRadius: 8,
                    padding: '14px 18px',
                    minWidth: 320,
                    maxWidth: 370,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    lineHeight: 1.6,
                }}>
                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                        {formula.label} {mathPopup.discipline && mathPopup.discipline !== '__header' ? `— ${mathPopup.discipline}` : ''}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: '#aaa' }}>Formula: </span>
                        <span style={{ color: '#f5c842', whiteSpace: 'pre-wrap' }}>{formula.formula}</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: '#aaa' }}>Source: </span>
                        <span style={{ color: '#82e0aa' }}>{formula.source}</span>
                    </div>
                    {detailText && (
                        <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', whiteSpace: 'pre-wrap', color: '#d5d8dc', fontSize: 11 }}>
                            {detailText}
                        </div>
                    )}
                    <div onClick={() => setMathPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>
                        ✕ Close
                    </div>
                </div>
            </>
        );
    };

    const InfoIcon = ({ onClick }) => (
        <span
            onClick={onClick}
            style={{ cursor: 'pointer', marginLeft: 3, fontSize: 10, color: '#2d6cdf', verticalAlign: 'super', fontWeight: 'normal' }}
            title="Click to see formula"
        >ⓘ</span>
    );

    // Budget overview rows: structure to match bidForecast page exactly
    const budgetColumns = [
        "K2 Budget",
        "Bid Budget",
        "Approved COs",
        "Current Budget",
        "Actual Costs",
        "Open Commit",
        "Total Commit",
        ...(etcBreakdownExpanded ? [
            "ETC Labor",
            "ETC Equipment",
            "ETC Materials",
            "ETC Rentals",
            "ETC Subs",
            "ETC Other",
        ] : []),
        "ETC Total",
        "EAC",
        "Δ EAC Budget",
        "Δ EAC Previous",
        // "Plan $/QTY",
        // "Actual $/QTY",
        // "Trend $/QTY",
    ];

    // Generate budgetRows dynamically from budgetData with cost code sub-rows
    const budgetRows = useMemo(() => {
        if (!jobUserData) return [{ label: "Contract Amount", level: 0 }];

        const rows = [{ label: "Contract Amount", level: 0 }];

        // Build discipline mapping from jobUserData
        const disciplineMapping = {};
        jobUserData
            .filter(item => item.Forecast_Tab === 'Productivity Tracker' && item.Forecast_Field_Name === 'discipline')
            .forEach(item => {
                disciplineMapping[item.Forecast_Row_ID] = item.Forecast_Value || 'Other';
            });

        // Collect all cost codes from various sources and add to disciplineMapping as 'Other' if not already present
        const allCostCodes = new Set();
        
        // From Day Zero Budget
        jobUserData.filter(item => item.Forecast_Tab === 'Day Zero Budget' && item.Cost_Code && item.Version_GUID === selectedVersion).forEach(item => {
            if (item.Cost_Code && !item.Cost_Type?.toString().startsWith('4') && item.Cost_Code !== '1000' && item.Cost_Code !== '990000') {
                allCostCodes.add(item.Cost_Code);
            }
        });
        ``
        // From CO Log
        jobUserData.filter(item => (item.Forecast_Tab === 'Change Order') && item.Cost_Code && item.Version_GUID === selectedVersion).forEach(item => {
            if (item.Cost_Code && !item.Cost_Type?.toString().startsWith('4') && item.Cost_Code !== '1000' && item.Cost_Code !== '990000') {
                allCostCodes.add(item.Cost_Code);
            }
        });
        
        // From ETC tabs
        jobUserData.filter(item => ['Materials', 'Rentals', 'Subcontractors', 'Other', 'Materials-phased', 'Rentals-phased', 'Subcontractors-phased', 'Other-phased', 'Burn Tool Labor', 'Burn Tool Equipment', 'Burn Tool Labor Phased', 'Burn Tool Equipment Phased'].includes(item.Forecast_Tab) && item.Cost_Code && item.Version_GUID === selectedVersion).forEach(item => {
            if (item.Cost_Code && !item.Cost_Type?.toString().startsWith('4') && item.Cost_Code !== '1000' && item.Cost_Code !== '990000') {
                allCostCodes.add(item.Cost_Code);
            }
        });
        
        // From jobCostDetail
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(item => {
                if (item.Phase_Code && !item.Cost_Type?.toString().startsWith('4') && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    allCostCodes.add(item.Phase_Code);
                }
            });
        }
        
        // From jobOpenCommitments
        if (Array.isArray(jobOpenCommitments)) {
            jobOpenCommitments.forEach(item => {
                if (item.Phase_Code && !item.Cost_Type?.toString().startsWith('4') && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    allCostCodes.add(item.Phase_Code);
                }
            });
        }
        
        // Add all collected cost codes to disciplineMapping as 'Other' if not already present
        allCostCodes.forEach(costCode => {
            if (!disciplineMapping[costCode]) {
                disciplineMapping[costCode] = 'Other';
            }
        });

        // Get cost code descriptions from Day Zero Budget in jobUserData or jobCostDetail
        const costCodeDescriptions = {};
        
        // First try to get from jobPhaseCodes atom
        if (jobPhaseCodes) {
            Object.keys(jobPhaseCodes).forEach(code => {
                costCodeDescriptions[code] = jobPhaseCodes[code];
            });
        }
        
        // Then supplement from jobCostDetail if available
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(item => {
                if (item.Phase_Code && item.Phase_Code_Description && !costCodeDescriptions[item.Phase_Code]) {
                    costCodeDescriptions[item.Phase_Code] = item.Phase_Code_Description;
                }
            });
        }

        console.log('allCostCodes', allCostCodes);
        console.log('costCodeDescriptions', costCodeDescriptions);


        // Group cost codes by discipline
        const disciplineGroups = {};
        Object.keys(disciplineMapping).forEach(costCode => {
            const discipline = disciplineMapping[costCode];
            if (discipline !== 'Contingency') { // Handle Contingency separately
                if (!disciplineGroups[discipline]) {
                    disciplineGroups[discipline] = [];
                }
                disciplineGroups[discipline].push({
                    code: costCode,
                    description: costCodeDescriptions[costCode] || costCode
                });
            }
        });

        // Add disciplines with their cost code sub-rows
        Object.keys(disciplineGroups).sort().forEach(discipline => {
            if (discipline !== 'Other') { // Add Other at the end
                rows.push({ label: discipline, level: 0, discipline: discipline });
                // Add cost code sub-rows sorted by code
                disciplineGroups[discipline]
                    .sort((a, b) => a.code.localeCompare(b.code))
                    .forEach(costCode => {
                        rows.push({
                            label: `  ${costCode.code}${costCode.description && costCode.description !== costCode.code ? ' - ' + costCode.description : ''}`,
                            level: 1,
                            costCode: costCode.code,
                            discipline: discipline
                        });
                    });
            }
        });

        // Add Other discipline if it exists
        if (disciplineGroups['Other']) {
            rows.push({ label: 'Other', level: 0, discipline: 'Other' });
            disciplineGroups['Other']
                .sort((a, b) => a.code.localeCompare(b.code))
                .forEach(costCode => {
                    rows.push({
                        label: `  ${costCode.code}${costCode.description && costCode.description !== costCode.code ? ' - ' + costCode.description : ''}`,
                        level: 1,
                        costCode: costCode.code,
                        discipline: 'Other'
                    });
                });
        }

        // Add Contingency and Total Costs at the end
        rows.push({ label: "Contingency", level: 0 });
        rows.push({ label: "Total Costs", level: 0 });

        return rows;
    }, [jobUserData, jobCostDetail, jobOpenCommitments, selectedVersion]);

    useEffect(() => {
        console.log('selectedJobDetails in Overview:', selectedJobDetails);
    }, [selectedJobDetails]);

    useEffect(() => {
        if (versions && versions.length > 0 && selectedVersion) {
            setVersionInfo(versions.find((v) => v.Version_GUID === selectedVersion) || {});
        }
    }, [versions, selectedVersion]);

    useEffect(() => {
        console.log('versionInfo in Overview:', versionInfo);
    }, [versionInfo]);

    // Calculate budget data from jobUserData, jobCostDetail, and jobOpenCommitments
    const calculatedBudgetData = useMemo(() => {
        if (!jobUserData || !jobCostDetail || !jobOpenCommitments) {
            return {};
        }

        const result = {};

        // Build discipline mapping and accumulate all cost code data in single pass through jobUserData
        const disciplineMapping = {};
        const costCodeData = {}; // Store ETC (by category), dayZero, and CO data by cost code
        const costCodeQuantities = {}; // Store quantities by cost code
        const installedByCode = {}; // Store installed quantities by cost code (from Scorecard)
        const coQuantitiesByCode = {}; // Store CO quantities by cost code
        const plugByCode = {}; // Store plug quantities by cost code (from Scorecard)
        const previousVersionData = {}; // Store previous version ETC data
        const previousActualCostsByCode = {}; // Store previous version actual costs
        const previousCommitmentsByCode = {}; // Store previous version open commitments
        const chOrValues = {}
        const dayZeroValues = {}
        let mechanicallyCompleteDates = {}
        let startDates = {}
        let completionDates = {}
        let physicalComplete = {}

        const phasedEquipment = {}
        const phasedLabor = {}
        const laborValues = {}
        const equipmentValues = {}
        const contingData = {}

        // Remove duplicates - keep only the most recently created/updated record for each unique combination
        // Group by unique key
        const groupedData = jobUserData.reduce((acc, item) => {
            const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Field_Name}_${item.Forecast_Row_ID}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});
        
        // For each group, keep only the most recently created/updated record
        const deduplicatedData = Object.values(groupedData).map(group => {
            return group.reduce((latest, current) => {
                const latestDate = new Date(Math.max(
                    new Date(latest.DSS_Created_Date),
                    new Date(latest.DSS_Updated_Datetime || 0)
                ));
                const currentDate = new Date(Math.max(
                    new Date(current.DSS_Created_Date),
                    new Date(current.DSS_Updated_Datetime || 0)
                ));
                return currentDate > latestDate ? current : latest;
            });
        });
        
        console.log('Original jobUserData length:', jobUserData.length, 'Deduplicated length:', deduplicatedData.length);

        deduplicatedData.forEach(item => {
            const d = item
            // Only process items for the selected version
            if (item.Version_GUID == selectedVersion) {
                // Build discipline mapping (skip if Cost_Type starts with 4*)
                if (item.Forecast_Tab === 'Productivity Tracker' && item.Forecast_Field_Name === 'discipline') {
                    disciplineMapping[item.Forecast_Row_ID] = item.Forecast_Value || 'Other';
                }

                // Collect installed quantities from Scorecard
                if (item.Forecast_Tab === 'Scorecard' && item.Forecast_Field_Name === 'installed') {
                    installedByCode[item.Forecast_Row_ID] = pNum(item.Forecast_Value || 0);
                }

                // Collect plug quantities from Scorecard
                if (item.Forecast_Tab === 'Scorecard' && item.Forecast_Field_Name === 'plug') {
                    plugByCode[item.Forecast_Row_ID] = pNum(item.Forecast_Value || 0);
                }

                if (d?.Forecast_Tab === 'Change Order') {
                    chOrValues[d?.Forecast_User_Data_GUID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                    // Collect CO quantities by cost code
                    if (d?.Cost_Code && d?.Cost_Code !== '1000' && d?.Cost_Code !== '990000' && d?.Status === 'Approved') {
                        const isRevenueCO = d?.Cost_Type && d?.Cost_Type.toString().startsWith('4');
                        const coKey = isRevenueCO ? 'CONTRACT_AMOUNT' : d?.Cost_Code;
                        if (!coQuantitiesByCode[coKey]) {
                            coQuantitiesByCode[coKey] = 0;
                        }
                        coQuantitiesByCode[coKey] += pNum(d?.Quantity || 0);
                    }
                }

                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    if (dayZeroValues[d?.Forecast_Row_ID]) {
                        dayZeroValues[d?.Forecast_Row_ID] = d
                    } else {
                        dayZeroValues[d?.Forecast_Row_ID] = d
                    }
                    
                    // Collect quantities for cost codes
                    if (d?.Cost_Code && d?.Cost_Code !== '1000' && d?.Cost_Code !== '990000') {
                        const isRevenue = d?.Cost_Type && d?.Cost_Type.toString().startsWith('4');
                        const key = isRevenue ? 'CONTRACT_AMOUNT' : d?.Cost_Code;
                        if (!costCodeQuantities[key]) {
                            costCodeQuantities[key] = 0;
                        }
                        costCodeQuantities[key] += pNum(d?.Quantity || 0);
                    }
                }

                if (d?.Forecast_Tab === 'All' || d?.Forecast_Tab === 'Forecast') {
                    if (d?.Forecast_Field_Name === 'mechanicallyComplete') {
                        mechanicallyCompleteDates = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                    } else if (d?.Forecast_Field_Name === 'startDateActual') {
                        startDates = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                    } else if (d?.Forecast_Field_Name === 'completionDateForecast') {
                        completionDates = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                    } else if (d?.Forecast_Field_Name === 'physicalComplete') {
                        physicalComplete = d?.Forecast_Value
                    }
                }

                if (d?.Forecast_Tab === 'Burn Tool Labor') {
                    if (laborValues[d?.Forecast_Row_ID]) {
                        laborValues[d?.Forecast_Row_ID] = d
                    } else {
                        laborValues[d?.Forecast_Row_ID] = d
                    }
                }

                if (d?.Forecast_Tab === 'Burn Tool Equipment') {
                    if (equipmentValues[d?.Forecast_Row_ID]) {
                        equipmentValues[d?.Forecast_Row_ID] = d
                    } else {
                        equipmentValues[d?.Forecast_Row_ID] = d
                    }
                }

                if (d?.Forecast_Tab === 'Burn Tool Equipment Phased') {
                    if (phasedEquipment[d?.Forecast_Row_ID]) {
                        phasedEquipment[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    } else {
                        phasedEquipment[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    }
                }

                if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    if (phasedLabor[d?.Forecast_Row_ID]) {
                        phasedLabor[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    } else {
                        phasedLabor[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    }
                }

                if (d?.Forecast_Tab === 'Contingency') {
                    if (contingData[d?.Forecast_Row_ID]) {
                        contingData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        contingData[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }
                
                if ((d.Forecast_Tab == 'Materials' || d.Forecast_Tab == 'Rentals' || d.Forecast_Tab === 'Subcontractors' || d.Forecast_Tab === 'Other' || d.Forecast_Tab === 'Materials-phased' || d.Forecast_Tab === 'Rentals-phased' || d.Forecast_Tab === 'Subcontractors-phased' || d.Forecast_Tab === 'Other-phased') && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                    if (d?.Cost_Code && d?.Cost_Code !== '1000' && d?.Cost_Code !== '990000') {
                        // Check if this is a revenue cost type (4*)
                        const isRevenue = d?.Cost_Type && d?.Cost_Type.toString().startsWith('4');
                        const key = isRevenue ? 'CONTRACT_AMOUNT' : d?.Cost_Code
                        
                        if (!costCodeData[key]) {
                            costCodeData[key] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        }
                        
                        const amount = pNum(d?.Value_Amount);
                        costCodeData[key].etc += amount;
                        
                        // Categorize by tab
                        if (d.Forecast_Tab === 'Materials' || d.Forecast_Tab === 'Materials-phased') {
                            costCodeData[key].etcMaterials += amount;
                        //console.log('Adding Materials ETC:', amount, 'to Cost Code:', key, d, costCodeData[key].etcMaterials);
                        } else if (d.Forecast_Tab === 'Rentals' || d.Forecast_Tab === 'Rentals-phased') {
                            costCodeData[key].etcRentals += amount;
                        } else if (d.Forecast_Tab === 'Subcontractors' || d.Forecast_Tab === 'Subcontractors-phased') {
                            costCodeData[key].etcSubs += amount;
                        } else if (d.Forecast_Tab === 'Other' || d.Forecast_Tab === 'Other-phased') {
                            costCodeData[key].etcOther += amount;
                        }
                    }
                }
            }
        });


        let regularTimeCostType = 0
        let equipmentCostType = 0
        let regularTime = 0
        let regularHours = 0
        const pC = completionDates


        equipmentValues && Object.keys(equipmentValues).forEach((key) => {
            if (key.length == 10 && moment(key).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(pC).endOf('week').isAfter(key)) {
                const row = equipmentValues?.[key]
                const equipmentCost = (isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours))
                equipmentCostType = equipmentCostType + equipmentCost
                
                // Add to costCodeData by Cost_Code
                if (row?.Cost_Code && equipmentCost > 0 && row?.Cost_Code !== '1000' && row?.Cost_Code !== '990000') {
                    const isRevenue = row?.Cost_Type && row?.Cost_Type.toString().startsWith('4');
                    const codeKey = isRevenue ? 'CONTRACT_AMOUNT' : row?.Cost_Code;
                    
                    if (!costCodeData[codeKey]) {
                        costCodeData[codeKey] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                    }
                    costCodeData[codeKey].etc += equipmentCost;
                    costCodeData[codeKey].etcEquipment += equipmentCost;
                }
            }
        })

        laborValues && Object.values(laborValues).forEach((row) => {
            if (row) {
                const week = row?.Forecast_Row_ID ? row.Forecast_Row_ID?.split('*')[0] : ''
                const pC = completionDates
                if (week && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(pC).isAfter(week)) {
                    const laborCost = !isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0
                    regularTimeCostType = regularTimeCostType + laborCost
                    regularHours = regularHours + (!isNaN(pNum(row.Number_Hours)) && !isNaN(pNum(row.Number_Units)) ? pNum(row.Number_Hours) * pNum(row.Number_Units) : 0)
                    
                    // Add to costCodeData by Cost_Code
                    if (row?.Cost_Code && laborCost > 0 && row?.Cost_Code !== '1000' && row?.Cost_Code !== '990000') {
                        const isRevenue = row?.Cost_Type && row?.Cost_Type.toString().startsWith('4');
                        const codeKey = isRevenue ? 'CONTRACT_AMOUNT' : row?.Cost_Code;
                        
                        if (!costCodeData[codeKey]) {
                            costCodeData[codeKey] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        }
                        costCodeData[codeKey].etc += laborCost;
                        costCodeData[codeKey].etcLabor += laborCost;
                    }
                }
            }
        })


        phasedLabor && Object.keys(phasedLabor).forEach((row) => {
            const weekRow = row.includes('=')
            const week = row.split('=')[1]
            const phase = row.split('=')[0]
            const pC = completionDates
            const phaseDetails = phase ? phasedLabor[phase] : {}
            const withinProject = moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pC).endOf('week'))
            const withinStartStop = true//moment(week).isAfter(moment(phaseDetails?.startDate).startOf('week')) && moment(week).isBefore(moment(phaseDetails?.endDate).endOf('week'))
            const h = phasedLabor[row]?.Number_Hours ? phasedLabor[row].Number_Hours : 0
            const bR = phasedLabor[row]?.Rate ? phasedLabor[row].Rate : 0
            const cS = phasedLabor[row]?.Number_Units ? phasedLabor[row].Number_Units : 0
            if (weekRow && phasedLabor[row] && h && bR && cS && withinProject && withinStartStop) {
                const phasedLaborCost = !isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0
                regularHours = regularHours + (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                regularTime = regularTime + phasedLaborCost
                
                // Add to costCodeData by Cost_Code
                const laborRow = phasedLabor[row]
                if (laborRow?.Cost_Code && phasedLaborCost > 0 && laborRow?.Cost_Code !== '1000' && laborRow?.Cost_Code !== '990000') {
                    const isRevenue = laborRow?.Cost_Type && laborRow?.Cost_Type.toString().startsWith('4');
                    const codeKey = isRevenue ? 'CONTRACT_AMOUNT' : laborRow?.Cost_Code;
                    
                    if (!costCodeData[codeKey]) {
                        costCodeData[codeKey] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                    }
                    costCodeData[codeKey].etc += phasedLaborCost;
                    costCodeData[codeKey].etcLabor += phasedLaborCost;
                }
            }
        })

        // Process phased equipment ETC
        phasedEquipment && Object.keys(phasedEquipment).forEach((row) => {
            //console.log('Processing phased equipment row:', row, phasedEquipment[row]);
            const weekRow = row.includes('*')
            const week = row.split('*')[1]
            const phase = row.split('*')[0]
            const pC = completionDates
            const withinProject = moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pC).endOf('week'))
            const withinStartStop = true//moment(week).isAfter(moment(phaseDetails?.startDate).startOf('week')) && moment(week).isBefore(moment(phaseDetails?.endDate).endOf('week'))
            const h = phasedEquipment[row]?.Number_Hours ? phasedEquipment[row].Number_Hours : 0
            const bR = phasedEquipment[row]?.Rate ? phasedEquipment[row].Rate : 0
            const cS = phasedEquipment[row]?.Number_Units ? phasedEquipment[row].Number_Units : 0
            //console.log('Phased Equipment Row:', weekRow, phasedEquipment[row], 'Within Project:', withinProject, 'Within Start/Stop:', withinStartStop, 'H:', h, 'BR:', bR, 'CS:', cS);
            if (weekRow && phasedEquipment[row] && h && bR && cS && withinProject && withinStartStop) {
                const phasedEquipmentCost = !isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0
                
                //console.log('Calculated Phased Equipment Cost:', phasedEquipmentCost, 'for row:', row);
                // Add to costCodeData by Cost_Code
                const equipmentRow = phasedEquipment[row]
                if (phasedEquipmentCost > 0) {
                    const codeKey = phase != 'undefined' ? phase : '100101'
                    
                    if (!costCodeData[codeKey]) {
                        costCodeData[codeKey] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                    }
                    costCodeData[codeKey].etc += phasedEquipmentCost;
                    costCodeData[codeKey].etcEquipment += phasedEquipmentCost;
                    //console.log('Adding Phased Equipment ETC:', phasedEquipmentCost, 'to Cost Code:', codeKey, equipmentRow, costCodeData[codeKey].etcEquipment);
                }
            }
        })
        
        // Collect previous version ETC data from jobUserDataPrev
        if (jobUserDataPrev && Array.isArray(jobUserDataPrev)) {
            jobUserDataPrev.forEach(d => {
                if ((d.Forecast_Tab === 'Materials' || d.Forecast_Tab === 'Rentals' || d.Forecast_Tab === 'Subcontractors' || 
                     d.Forecast_Tab === 'Other' || d.Forecast_Tab === 'Materials-phased' || d.Forecast_Tab === 'Rentals-phased' || 
                     d.Forecast_Tab === 'Subcontractors-phased' || d.Forecast_Tab === 'Other-phased' || 
                     d.Forecast_Tab === 'Burn Tool Labor' || d.Forecast_Tab === 'Burn Tool Equipment' || 
                     d.Forecast_Tab === 'Burn Tool Labor Phased' || d.Forecast_Tab === 'Burn Tool Equipment Phased')) {
                    if (d?.Cost_Code && d?.Cost_Code !== '1000' && d?.Cost_Code !== '990000') {
                        const isRevenue = d?.Cost_Type && d?.Cost_Type.toString().startsWith('4');
                        const key = isRevenue ? 'CONTRACT_AMOUNT' : d?.Cost_Code;
                        if (!previousVersionData[key]) {
                            previousVersionData[key] = 0;
                        }
                        previousVersionData[key] += pNum(d?.Value_Amount || 0);
                    }
                }
            });
        }

        let totalcLabor = 0
        let totalcEquipment = 0
        let totalcMaterials = 0
        let totalcSubcontractors = 0
        let totalcOtherExpenses = 0
        let totalcTotal = 0

        contingData && Object.values(contingData).forEach((row) => {
            totalcLabor = totalcLabor + (isNaN(pNum(row.CurrentLabor)) ? 0 : pNum(row.CurrentLabor))
            totalcEquipment = totalcEquipment + (isNaN(pNum(row.CurrentEquipment)) ? 0 : pNum(row.CurrentEquipment))
            totalcMaterials = totalcMaterials + (isNaN(pNum(row.CurrentMaterials)) ? 0 : pNum(row.CurrentMaterials))
            totalcSubcontractors = totalcSubcontractors + (isNaN(pNum(row.CurrentSubs)) ? 0 : pNum(row.CurrentSubs))
            totalcOtherExpenses = totalcOtherExpenses + (isNaN(pNum(row.CurrentOther)) ? 0 : pNum(row.CurrentOther))
        })
        totalcTotal = totalcLabor + totalcEquipment + totalcMaterials + totalcSubcontractors + totalcOtherExpenses

        // Get Contingency (101101) Day Zero and COs
        let contingencyDayZeroTotal = 0;
        let contingencyCOTotal = 0;
        
        dayZeroValues && Object.values(dayZeroValues).forEach((row) => {
            if (row['Cost_Code'] === '101101') {
                contingencyDayZeroTotal += pNum(row.Value_Amount || 0);
            }
        });
        
        chOrValues && Object.values(chOrValues).forEach((row) => {
            if (row.Status === 'Approved' && row['Cost_Code'] === '101101') {
                contingencyCOTotal += (isNaN(pNum(row['41000'])) ? 0 : pNum(row['41000']));
                // contingencyCOTotal += (isNaN(pNum(row.tAndM)) ? 0 : pNum(row.tAndM));
                // contingencyCOTotal += (isNaN(pNum(row.changeOrders)) ? 0 : pNum(row.changeOrders));
                // contingencyCOTotal += (isNaN(pNum(row.reductionOfScope)) ? 0 : pNum(row.reductionOfScope));
            }
        });
        
        costCodeData['Contingency'] = {
            etcLabor: totalcLabor,
            etcEquipment: totalcEquipment,
            etcMaterials: totalcMaterials,
            etcSubs: totalcSubcontractors,
            etcOther: totalcOtherExpenses,
            etc: totalcTotal,
            dayZero: contingencyDayZeroTotal,
            dZ: [],
            cos: contingencyCOTotal
        }

        // Accumulate Day Zero Budget and Change Orders by cost code
        dayZeroValues && Object.values(dayZeroValues).forEach((row) => {
            if (row['Cost_Code'] && row['Cost_Code'] !== '1000' && row['Cost_Code'] !== '990000') {
                // Check if this is a revenue cost type (4*)
                const isRevenue = row['Cost_Type'] && row['Cost_Type'].toString().startsWith('4');
                const key = isRevenue ? 'CONTRACT_AMOUNT' : row['Cost_Code'];
                
                if (!costCodeData[key]) {
                    costCodeData[key] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                }
                costCodeData[key].dZ.push(row);
            }
        })

        chOrValues && Object.values(chOrValues).forEach((row) => {
            if (row.Status === 'Approved' && row['Cost_Code'] && row['Cost_Code'] !== '1000' && row['Cost_Code'] !== '990000') {
                // Check if this is a revenue cost type (4*)
                const isRevenue = row['Cost_Type'] && row['Cost_Type'].toString().startsWith('4');
                const key = isRevenue ? 'CONTRACT_AMOUNT' : row['Cost_Code'];
                
                if (!costCodeData[key]) {
                    costCodeData[key] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                }
                
                costCodeData[key].cos += (isNaN(pNum(row['41000'])) ? 0 : pNum(row['41000']));
                // costCodeData[key].cos += (isNaN(pNum(row.tAndM)) ? 0 : pNum(row.tAndM));
                // costCodeData[key].cos += (isNaN(pNum(row.changeOrders)) ? 0 : pNum(row.changeOrders));
                // costCodeData[key].cos += (isNaN(pNum(row.reductionOfScope)) ? 0 : pNum(row.reductionOfScope));
            }
        })


        Object.keys(costCodeData).forEach(costCode => {
            if (costCode !== 'CONTRACT_AMOUNT' && !disciplineMapping[costCode]) {
                disciplineMapping[costCode] = 'Other';
            }
        });

        // Build actual costs by cost code from jobCostDetail (single pass)
        const actualCostsByCode = {};
        jobCostDetail.forEach(item => {
            if (item.Phase_Code && item.Cost_Type_Code[0] == '5' && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                const key = item.Phase_Code;
                const previousValue = actualCostsByCode[key] || 0;
                const newAmount = pNum(item.Job_Cost || 0);
                actualCostsByCode[key] = previousValue + newAmount;
                
                // Add to disciplineMapping if not already present (as Other)
                if (!disciplineMapping[key]) {
                    disciplineMapping[key] = 'Other';
                }
            }
        });
        
        // Build previous version actual costs from jobCostDetailPrev
        console.log('jobCostDetailPrev length:', jobCostDetailPrev?.length);
        if (jobCostDetailPrev && Array.isArray(jobCostDetailPrev)) {
            jobCostDetailPrev.forEach(item => {
                if (item.Phase_Code && item.Cost_Type_Code && item.Cost_Type_Code[0] == '5' && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const key = item.Phase_Code;
                    const previousValue = previousActualCostsByCode[key] || 0;
                    const newAmount = pNum(item.Job_Cost || 0);
                    previousActualCostsByCode[key] = previousValue + newAmount;
                    //console.log('Adding prev actual:', key, newAmount, 'total:', previousActualCostsByCode[key]);
                }
            });
        }
        console.log('Final previousActualCostsByCode:', Object.keys(previousActualCostsByCode).length, 'entries');

        // Build commitments by cost code from jobOpenCommitments (single pass)
        const commitmentsByCode = {};
        jobOpenCommitments.forEach(item => {
            if (item.Phase_Code && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {

                const key = item.Phase_Code;
                const previousValue = commitmentsByCode[key] || 0;
                const newAmount = pNum(item.PO_True_Open_Amount || 0);
                commitmentsByCode[key] = previousValue + newAmount;
                
                // Add to disciplineMapping if not already present (as Other)
                if (!disciplineMapping[key]) {
                    disciplineMapping[key] = 'Other';
                }
            }
        });
        
        // Build previous version commitments from jobOpenCommitmentsPrev
        if (jobOpenCommitmentsPrev && Array.isArray(jobOpenCommitmentsPrev)) {
            jobOpenCommitmentsPrev.forEach(item => {
                if (item.Phase_Code && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const key = item.Phase_Code;
                    const previousValue = previousCommitmentsByCode[key] || 0;
                    const newAmount = pNum(item.PO_True_Open_Amount || 0);
                    previousCommitmentsByCode[key] = previousValue + newAmount;
                }
            });
        }

        console.log('prev act/open', previousActualCostsByCode, previousCommitmentsByCode);

        // Get all unique disciplines
        const allDisciplines = [...new Set(Object.values(disciplineMapping))];
        if (!allDisciplines.includes('Other')) {
            allDisciplines.push('Other');
        }

        // Get cost codes for each discipline
        const disciplineCostCodes = {};
        Object.keys(disciplineMapping).forEach(costCode => {
            const discipline = disciplineMapping[costCode];
            if (!disciplineCostCodes[discipline]) {
                disciplineCostCodes[discipline] = [];
            }
            disciplineCostCodes[discipline].push(costCode);
        });

        // Calculate for each cost code first
        Object.keys(disciplineMapping).forEach(costCode => {
            const codeData = costCodeData[costCode] || { etc: 0, dayZero: 0, dZ: [], cos: 0 };
            const codeActualCosts = actualCostsByCode[costCode] || 0;
            const codeOpenCommit = commitmentsByCode[costCode] || 0;
            const codeETC = codeData.etc;
            const codeDayZeroBudget = codeData.dZ.reduce((sum, item) => sum + pNum(item.Value_Amount || 0), 0);
            const codeApprovedCOs = codeData.cos;
            const codeCurrentBudget = codeDayZeroBudget + codeApprovedCOs;
            const codeEAC = codeActualCosts + codeOpenCommit + codeETC;
            const codeDeltaEACBudget = codeEAC - codeCurrentBudget;
            
            // Calculate previous version EAC for △ EAC Previous
            const codePreviousActual = previousActualCostsByCode[costCode] || 0;
            const codePreviousCommit = previousCommitmentsByCode[costCode] || 0;
            const codePreviousETC = previousVersionData[costCode] || 0;
            const codePreviousEAC = codePreviousActual + codePreviousCommit + codePreviousETC;
            const codeDeltaEACPrevious = codeEAC - codePreviousEAC;
            
            // Get quantity for this cost code
            const codeQuantity = costCodeQuantities[costCode] || 0;
            
            // Calculate $/QTY metrics
            const codePlanPerQty = codeQuantity > 0 ? codeCurrentBudget / codeQuantity : 0;
            const codeActualPerQty = codeQuantity > 0 ? codeActualCosts / codeQuantity : 0;
            const codeTrendPerQty = codeQuantity > 0 ? (codeActualCosts + codeOpenCommit + codeETC) / codeQuantity : 0;
            
            // Calculate performance metrics - PF should use hours budget/eac instead of dollars
            // Apply 25% rule: if percent complete < 25%, PF = 1
            const codeInstalledQty = installedByCode[costCode] || 0;
            const codeTotalQty = (costCodeQuantities[costCode] || 0) + (coQuantitiesByCode[costCode] || 0) + (plugByCode[costCode] || 0);
            const codePercentComplete = codeTotalQty > 0 ? (codeInstalledQty / codeTotalQty) * 100 : 0;
            const codeHoursBudget = codeData.dZ.reduce((sum, item) => sum + pNum(item.Hours || 0), 0);
            const codeHoursEAC = codeHoursBudget > 0 ? (codeEAC / codeCurrentBudget) * codeHoursBudget : 0;
            const codePF = codePercentComplete < 25 ? 1 : (codeHoursBudget > 0 ? codeHoursEAC / codeHoursBudget : 0); // Performance Factor with 25% rule
            const codeCPI = codeActualCosts > 0 ? codeCurrentBudget / codeActualCosts : 0; // Cost Performance Index
            const codeSPI = codeCurrentBudget > 0 && codeEAC > 0 ? codeCurrentBudget / codeEAC : 0; // Schedule Performance Index

            result[`costCode_${costCode}`] = {
                k2Budget: '-',
                bidBudget: fNum(codeDayZeroBudget, 1),
                approvedCOs: fNum(codeApprovedCOs, 1),
                currentBudget: fNum(codeCurrentBudget, 1),
                actualCosts: fNum(codeActualCosts, 1),
                openCommit: fNum(codeOpenCommit, 1),
                totalCommit: fNum(codeActualCosts + codeOpenCommit, 1),
                etcLabor: fNum(codeData.etcLabor || 0, 1),
                etcEquipment: fNum(codeData.etcEquipment || 0, 1),
                etcMaterials: fNum(codeData.etcMaterials || 0, 1),
                etcRentals: fNum(codeData.etcRentals || 0, 1),
                etcSubs: fNum(codeData.etcSubs || 0, 1),
                etcOther: fNum(codeData.etcOther || 0, 1),
                etc: fNum(codeETC, 1),
                eac: fNum(codeEAC, 1),
                deltaEACBudget: fNum(codeDeltaEACBudget, 1),
                deltaEACPrevious: fNum(codeDeltaEACPrevious, 1),
                planPerQty: codeQuantity > 0 ? fNum(codePlanPerQty, 2) : '-',
                actualPerQty: codeQuantity > 0 ? fNum(codeActualPerQty, 2) : '-',
                trendPerQty: codeQuantity > 0 ? fNum(codeTrendPerQty, 2) : '-',
                pf: fNum(codePF, 2),
                cpi: fNum(codeCPI, 2),
                spi: fNum(codeSPI, 2)
            };
        });

        // Aggregate by discipline
        allDisciplines.forEach(discipline => {
            const costCodes = disciplineCostCodes[discipline] || [];
            let actualCosts = 0;
            let openCommit = 0;
            let etc = 0;
            let etcLabor = 0;
            let etcEquipment = 0;
            let etcMaterials = 0;
            let etcRentals = 0;
            let etcSubs = 0;
            let etcOther = 0;
            let dayZeroBudget = 0;
            let approvedCOs = 0;

            costCodes.forEach(costCode => {
                const codeData = costCodeData[costCode] || { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                const actualForCode = actualCostsByCode[costCode] || 0;
                const commitForCode = commitmentsByCode[costCode] || 0;
            
                
                actualCosts += actualForCode;
                openCommit += commitForCode;
                etc += codeData.etc;
                etcLabor += codeData.etcLabor || 0;
                etcEquipment += codeData.etcEquipment || 0;
                etcMaterials += codeData.etcMaterials || 0;
                etcRentals += codeData.etcRentals || 0;
                etcSubs += codeData.etcSubs || 0;
                etcOther += codeData.etcOther || 0;
                dayZeroBudget += codeData.dZ.reduce((sum, item) => sum + pNum(item.Value_Amount || 0), 0);
                approvedCOs += codeData.cos;
            });

            const currentBudget = dayZeroBudget + approvedCOs;
            const eac = actualCosts + openCommit + etc;
            const deltaEACBudget = eac - currentBudget;
            
            // Calculate previous version EAC for discipline
            let previousActual = 0;
            let previousCommit = 0;
            let previousETC = 0;
            let disciplineQuantity = 0;
            costCodes.forEach(costCode => {
                previousActual += previousActualCostsByCode[costCode] || 0;
                previousCommit += previousCommitmentsByCode[costCode] || 0;
                previousETC += previousVersionData[costCode] || 0;
                disciplineQuantity += costCodeQuantities[costCode] || 0;
            });
            const previousEAC = previousActual + previousCommit + previousETC;
            const deltaEACPrevious = eac - previousEAC;
            
            // Calculate $/QTY metrics
            const planPerQty = disciplineQuantity > 0 ? currentBudget / disciplineQuantity : 0;
            const actualPerQty = disciplineQuantity > 0 ? actualCosts / disciplineQuantity : 0;
            const trendPerQty = disciplineQuantity > 0 ? eac / disciplineQuantity : 0;
            
            // Calculate performance metrics - apply 25% rule: if percent complete < 25%, PF = 1
            let disciplineInstalledQtyBudget = 0;
            let disciplineTotalQtyBudget = 0;
            costCodes.forEach(costCode => {
                disciplineInstalledQtyBudget += installedByCode[costCode] || 0;
                disciplineTotalQtyBudget += (costCodeQuantities[costCode] || 0) + (coQuantitiesByCode[costCode] || 0) + (plugByCode[costCode] || 0);
            });
            const disciplinePercentComplete = disciplineTotalQtyBudget > 0 ? (disciplineInstalledQtyBudget / disciplineTotalQtyBudget) * 100 : 0;
            const pf = disciplinePercentComplete < 25 ? 1 : (currentBudget > 0 ? eac / currentBudget : 0);
            const cpi = actualCosts > 0 ? currentBudget / actualCosts : 0;
            const spi = currentBudget > 0 && eac > 0 ? currentBudget / eac : 0;

            result[discipline] = {
                k2Budget: '-',
                bidBudget: fNum(dayZeroBudget, 1),
                approvedCOs: fNum(approvedCOs, 1),
                currentBudget: fNum(currentBudget, 1),
                actualCosts: fNum(actualCosts, 1),
                openCommit: fNum(openCommit, 1),
                totalCommit: fNum(actualCosts + openCommit, 1),
                etcLabor: fNum(etcLabor, 1),
                etcEquipment: fNum(etcEquipment, 1),
                etcMaterials: fNum(etcMaterials, 1),
                etcRentals: fNum(etcRentals, 1),
                etcSubs: fNum(etcSubs, 1),
                etcOther: fNum(etcOther, 1),
                etc: fNum(etc, 1),
                eac: fNum(eac, 1),
                deltaEACBudget: fNum(deltaEACBudget, 1),
                deltaEACPrevious: fNum(deltaEACPrevious, 1),
                planPerQty: disciplineQuantity > 0 ? fNum(planPerQty, 2) : '-',
                actualPerQty: disciplineQuantity > 0 ? fNum(actualPerQty, 2) : '-',
                trendPerQty: disciplineQuantity > 0 ? fNum(trendPerQty, 2) : '-',
                pf: fNum(pf, 2),
                cpi: fNum(cpi, 2),
                spi: fNum(spi, 2)
            };
        });

        // Calculate Contract Amount (revenue) - use accumulated 4* cost types
        const contractData = costCodeData['CONTRACT_AMOUNT'] || { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
        const contractDayZero = contractData.dZ.reduce((sum, item) => sum + pNum(item.Value_Amount || 0), 0);
        const contractCOs = contractData.cos;
        const contractActual = actualCostsByCode['CONTRACT_AMOUNT'] || 0;
        const contractCommit = commitmentsByCode['CONTRACT_AMOUNT'] || 0;
        const contractETC = contractData.etc;
        const contractCurrentBudget = contractDayZero + contractCOs;
        const contractEAC = contractActual + contractCommit + contractETC;
        
        // Previous version data for Contract Amount
        const contractPreviousActual = previousActualCostsByCode['CONTRACT_AMOUNT'] || 0;
        const contractPreviousCommit = previousCommitmentsByCode['CONTRACT_AMOUNT'] || 0;
        const contractPreviousETC = previousVersionData['CONTRACT_AMOUNT'] || 0;
        const contractPreviousEAC = contractPreviousActual + contractPreviousCommit + contractPreviousETC;
        const contractDeltaEACPrevious = contractEAC - contractPreviousEAC;
        
        const contractQuantity = costCodeQuantities['CONTRACT_AMOUNT'] || 0;
        const contractPlanPerQty = contractQuantity > 0 ? contractCurrentBudget / contractQuantity : 0;
        const contractActualPerQty = contractQuantity > 0 ? contractActual / contractQuantity : 0;
        const contractTrendPerQty = contractQuantity > 0 ? contractEAC / contractQuantity : 0;
        
        const contractPF = contractCurrentBudget > 0 ? contractEAC / contractCurrentBudget : 0;
        const contractCPI = contractActual > 0 ? contractCurrentBudget / contractActual : 0;
        const contractSPI = contractCurrentBudget > 0 && contractEAC > 0 ? contractCurrentBudget / contractEAC : 0;

        result['Contract Amount'] = {
            k2Budget: '-',
            bidBudget: fNum(contractDayZero, 1),
            approvedCOs: fNum(contractCOs, 1),
            currentBudget: fNum(contractCurrentBudget, 1),
            actualCosts: fNum(contractActual, 1),
            openCommit: fNum(contractCommit, 1),
            totalCommit: fNum(contractActual + contractCommit, 1),
            etcLabor: fNum(contractData.etcLabor || 0, 1),
            etcEquipment: fNum(contractData.etcEquipment || 0, 1),
            etcMaterials: fNum(contractData.etcMaterials || 0, 1),
            etcRentals: fNum(contractData.etcRentals || 0, 1),
            etcSubs: fNum(contractData.etcSubs || 0, 1),
            etcOther: fNum(contractData.etcOther || 0, 1),
            etc: fNum(contractETC, 1),
            eac: fNum(contractEAC, 1),
            deltaEACBudget: fNum(contractEAC - contractCurrentBudget, 1),
            deltaEACPrevious: fNum(contractDeltaEACPrevious, 1),
            planPerQty: contractQuantity > 0 ? fNum(contractPlanPerQty, 2) : '-',
            actualPerQty: contractQuantity > 0 ? fNum(contractActualPerQty, 2) : '-',
            trendPerQty: contractQuantity > 0 ? fNum(contractTrendPerQty, 2) : '-',
            pf: fNum(contractPF, 2),
            cpi: fNum(contractCPI, 2),
            spi: fNum(contractSPI, 2)
        };

        // Calculate Contingency - use values directly from contingData
        const contingencyData = costCodeData['Contingency'] || { 
            etcLabor: 0, 
            etcEquipment: 0, 
            etcMaterials: 0, 
            etcSubs: 0, 
            etcOther: 0, 
            etc: 0 
        };
        
        const contingencyActual = 0; // No actual costs for contingency
        const contingencyCommit = 0; // No commitments for contingency
        const contingencyETC = contingencyData.etc;
        const contingencyEtcLabor = contingencyData.etcLabor;
        const contingencyEtcEquipment = contingencyData.etcEquipment;
        const contingencyEtcMaterials = contingencyData.etcMaterials;
        const contingencyEtcRentals = 0; // No rentals in contingency
        const contingencyEtcSubs = contingencyData.etcSubs;
        const contingencyEtcOther = contingencyData.etcOther;
        const contingencyDayZero = contingencyData.dayZero || 0;
        const contingencyCOs = contingencyData.cos || 0;

        const contingencyCurrentBudget = contingencyDayZero + contingencyCOs;
        const contingencyEAC = contingencyActual + contingencyCommit + contingencyETC;
        
        // Contingency typically has no previous version data or quantities
        const contingencyPF = contingencyCurrentBudget > 0 ? contingencyEAC / contingencyCurrentBudget : 0;
        const contingencyCPI = contingencyActual > 0 ? contingencyCurrentBudget / contingencyActual : 1;
        const contingencySPI = contingencyCurrentBudget > 0 && contingencyEAC > 0 ? contingencyCurrentBudget / contingencyEAC : 1;

        result['Contingency'] = {
            k2Budget: '-',
            bidBudget: fNum(contingencyDayZero, 1),
            approvedCOs: fNum(contingencyCOs, 1),
            currentBudget: fNum(contingencyCurrentBudget, 1),
            actualCosts: fNum(contingencyActual, 1),
            openCommit: fNum(contingencyCommit, 1),
            totalCommit: fNum(contingencyActual + contingencyCommit, 1),
            etcLabor: fNum(contingencyEtcLabor, 1),
            etcEquipment: fNum(contingencyEtcEquipment, 1),
            etcMaterials: fNum(contingencyEtcMaterials, 1),
            etcRentals: fNum(contingencyEtcRentals, 1),
            etcSubs: fNum(contingencyEtcSubs, 1),
            etcOther: fNum(contingencyEtcOther, 1),
            etc: fNum(contingencyETC, 1),
            eac: fNum(contingencyEAC, 1),
            deltaEACBudget: fNum(contingencyEAC - contingencyCurrentBudget, 1),
            deltaEACPrevious: '-',
            planPerQty: '-',
            actualPerQty: '-',
            trendPerQty: '-',
            pf: fNum(contingencyPF, 2),
            cpi: fNum(contingencyCPI, 2),
            spi: fNum(contingencySPI, 2)
        };

        // Calculate Total Costs (sum only discipline-level rows, not individual cost codes)
        // Filter to only include disciplines that have result entries (exclude cost codes)
        const disciplineKeys = allDisciplines.filter(discipline => result[discipline] && discipline !== 'Contingency');
        
        const totalActual = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.actualCosts || 0), 0);
        const totalCommitments = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.openCommit || 0), 0);
        const totalETC = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etc || 0), 0);
        const totalEtcLabor = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etcLabor || 0), 0);
        const totalEtcEquipment = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etcEquipment || 0), 0);
        const totalEtcMaterials = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etcMaterials || 0), 0);
        const totalEtcRentals = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etcRentals || 0), 0);
        const totalEtcSubs = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etcSubs || 0), 0);
        const totalEtcOther = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.etcOther || 0), 0);
        const totalEAC = totalActual + totalCommitments + totalETC;
        const totalDayZero = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.bidBudget || 0), 0);
        const totalCOs = disciplineKeys.reduce((sum, discipline) => sum + pNum(result[discipline]?.approvedCOs || 0), 0);
        const totalCurrentBudget = totalDayZero + totalCOs;
        
        // Calculate totals for previous version and quantities
        let totalPreviousActual = 0;
        let totalPreviousCommit = 0;
        let totalPreviousETC = 0;
        let totalQuantity = 0;
        Object.keys(disciplineMapping).forEach(costCode => {
            if (disciplineKeys.includes(disciplineMapping[costCode])) {
                totalPreviousActual += previousActualCostsByCode[costCode] || 0;
                totalPreviousCommit += previousCommitmentsByCode[costCode] || 0;
                totalPreviousETC += previousVersionData[costCode] || 0;
                totalQuantity += costCodeQuantities[costCode] || 0;
            }
        });
        const totalPreviousEAC = totalPreviousActual + totalPreviousCommit + totalPreviousETC;
        const totalDeltaEACPrevious = totalEAC - totalPreviousEAC;
        
        const totalPlanPerQty = totalQuantity > 0 ? totalCurrentBudget / totalQuantity : 0;
        const totalActualPerQty = totalQuantity > 0 ? totalActual / totalQuantity : 0;
        const totalTrendPerQty = totalQuantity > 0 ? totalEAC / totalQuantity : 0;
        
        // Apply 25% rule to total PF: weighted average of discipline PFs
        let totalWeightedPF = 0;
        let totalPFWeight = 0;
        disciplineKeys.forEach(discipline => {
            const discBudget = pNum(result[discipline]?.currentBudget || 0);
            const discPF = pNum(result[discipline]?.pf || 0);
            if (discBudget > 0) {
                totalWeightedPF += discPF * discBudget;
                totalPFWeight += discBudget;
            }
        });
        const totalPF = totalPFWeight > 0 ? totalWeightedPF / totalPFWeight : 0;
        const totalCPI = totalActual > 0 ? totalCurrentBudget / totalActual : 0;
        const totalSPI = totalCurrentBudget > 0 && totalEAC > 0 ? totalCurrentBudget / totalEAC : 0;

        result['Total Costs'] = {
            k2Budget: '-',
            bidBudget: fNum(totalDayZero, 1),
            approvedCOs: fNum(totalCOs, 1),
            currentBudget: fNum(totalCurrentBudget, 1),
            actualCosts: fNum(totalActual, 1),
            openCommit: fNum(totalCommitments, 1),
            totalCommit: fNum(totalActual + totalCommitments, 1),
            etcLabor: fNum(totalEtcLabor, 1),
            etcEquipment: fNum(totalEtcEquipment, 1),
            etcMaterials: fNum(totalEtcMaterials, 1),
            etcRentals: fNum(totalEtcRentals, 1),
            etcSubs: fNum(totalEtcSubs, 1),
            etcOther: fNum(totalEtcOther, 1),
            etc: fNum(totalETC, 1),
            eac: fNum(totalEAC, 1),
            deltaEACBudget: fNum(totalEAC - totalCurrentBudget, 1),
            deltaEACPrevious: fNum(totalDeltaEACPrevious, 1),
            planPerQty: totalQuantity > 0 ? fNum(totalPlanPerQty, 2) : '-',
            actualPerQty: totalQuantity > 0 ? fNum(totalActualPerQty, 2) : '-',
            trendPerQty: totalQuantity > 0 ? fNum(totalTrendPerQty, 2) : '-',
            pf: fNum(totalPF, 2),
            cpi: fNum(totalCPI, 2),
            spi: fNum(totalSPI, 2)
        };

        // Add K2 Budget data if available
        if (k2Data) {
            const k2Contract = k2Data.Estimated_Project_Amount || 0;
            const k2TotalCosts = k2Data.Expected_Cost || 0;

            result['Contract Amount'].k2Budget = fNum(k2Contract, 1);
            result['Total Costs'].k2Budget = fNum(k2TotalCosts, 1);
        }

        //console.log('Calculated Budget/Cost Data:', result);

        return result;
    }, [jobUserData, jobCostDetail, jobOpenCommitments, dayZero, selectedJobDetails, k2Data, jobCostDetailPrev, jobOpenCommitmentsPrev]);

    useEffect(() => {
        setBudgetData(calculatedBudgetData);
    }, [calculatedBudgetData]);

    // Change Order summary by status
    const changeOrderSummary = useMemo(() => {
        if (!jobUserData) return { approved: 0, pendingPerformed: 0, pendingNotPerformed: 0, notSubmitted: 0, denied: 0 };
        const summary = { approved: 0, pendingPerformed: 0, pendingNotPerformed: 0, notSubmitted: 0, denied: 0 };
        const dedup = {};
        jobUserData.forEach(item => {
            if (item.Version_GUID == selectedVersion && item.Forecast_Tab === 'Change Order') {
                const key = item.Forecast_User_Data_GUID;
                if (!dedup[key] || new Date(item.DSS_Updated_Datetime || item.DSS_Created_Date) > new Date(dedup[key].DSS_Updated_Datetime || dedup[key].DSS_Created_Date)) {
                    dedup[key] = item;
                }
            }
        });
        Object.values(dedup).forEach(row => {
            const amt = parseFloat(row.Value_Amount) || 0;
            switch (row.Status) {
                case 'Approved': summary.approved += amt; break;
                case 'Pending Performed': summary.pendingPerformed += amt; break;
                case 'Pending Not Performed': summary.pendingNotPerformed += amt; break;
                case 'Not Submitted': summary.notSubmitted += amt; break;
                case 'Denied': summary.denied += amt; break;
                default: break;
            }
        });
        return summary;
    }, [jobUserData, selectedVersion]);

    // Job Profit & JP% computed from budget data
    const jobProfitData = useMemo(() => {
        if (!budgetData || !budgetData['Contract Amount'] || !budgetData['Total Costs']) return null;
        const contractEAC = pNum(budgetData['Contract Amount']?.eac || '0');
        const totalCostsEAC = pNum(budgetData['Total Costs']?.eac || '0');
        const contractBudget = pNum(budgetData['Contract Amount']?.currentBudget || '0');
        const totalCostsBudget = pNum(budgetData['Total Costs']?.currentBudget || '0');
        const contractActual = pNum(budgetData['Contract Amount']?.actualCosts || '0');
        const totalCostsActual = pNum(budgetData['Total Costs']?.actualCosts || '0');

        const jpEAC = contractEAC - totalCostsEAC;
        const jpBudget = contractBudget - totalCostsBudget;
        const jpActual = contractActual - totalCostsActual;
        const jpPctEAC = contractEAC !== 0 ? (jpEAC / contractEAC) * 100 : 0;
        const jpPctBudget = contractBudget !== 0 ? (jpBudget / contractBudget) * 100 : 0;
        const jpPctActual = contractActual !== 0 ? (jpActual / contractActual) * 100 : 0;

        return {
            jpBudget: fNum(jpBudget, 1),
            jpActual: fNum(jpActual, 1),
            jpEAC: fNum(jpEAC, 1),
            jpPctBudget: jpPctBudget.toFixed(1) + '%',
            jpPctActual: jpPctActual.toFixed(1) + '%',
            jpPctEAC: jpPctEAC.toFixed(1) + '%',
        };
    }, [budgetData]);

    const exportBudgetToExcel = () => {
        // Prepare data for Excel
        const excelData = [];
        
        // Add header row
        excelData.push(['Job Cost', ...budgetColumns]);
        
        // Add data rows
        budgetRows.forEach(row => {
            const getDataKey = (row) => {
                if (row.label === 'Contract Amount') return 'Contract Amount';
                if (row.label === 'Total Costs') return 'Total Costs';
                if (row.label === 'Contingency') return 'Contingency';
                if (row.costCode) return `costCode_${row.costCode}`;
                if (row.discipline && calculatedBudgetData[row.discipline]) return row.discipline;
                return null;
            };
            
            const dataKey = getDataKey(row);
            const rowData = dataKey ? calculatedBudgetData[dataKey] : null;
            
            excelData.push([
                row.label,
                rowData?.k2Budget || '-',
                rowData?.bidBudget || '-',
                rowData?.approvedCOs || '-',
                rowData?.currentBudget || '-',
                rowData?.actualCosts || '-',
                rowData?.openCommit || '-',
                rowData?.totalCommit || '-',
                rowData?.etcLabor || '-',
                rowData?.etcEquipment || '-',
                rowData?.etcMaterials || '-',
                rowData?.etcRentals || '-',
                rowData?.etcSubs || '-',
                rowData?.etcOther || '-',
                rowData?.etc || '-',
                rowData?.eac || '-',
                rowData?.deltaEACBudget || '-',
                rowData?.deltaEACPrevious || '-',
                rowData?.planPerQty || '-',
                rowData?.actualPerQty || '-',
                rowData?.trendPerQty || '-',
                rowData?.pf || '-',
                rowData?.cpi || '-',
                rowData?.spi || '-'
            ]);
        });
        
        // Create workbook and worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Budget Overview');
        
        // Generate filename with job and version info
        const fileName = `Budget_Overview_${selectedJob}_${versionInfo?.Version_Name || 'version'}_${moment().format('YYYYMMDD')}.xlsx`;
        
        // Download file
        XLSX.writeFile(wb, fileName);
    };

    const exportOverviewToExcel = async () => {
        try {
            // Prepare overview data payload
            const overviewData = {
                metadata: {
                    jobId: selectedJob || '',
                    jobName: selectedJobDetails?.BU_Description || '',
                    versionTimestamp: versionInfo?.Version_Start_DateTime ? 
                        moment(versionInfo.Version_Start_DateTime).format('YYYY-MM-DD') : ''
                },
                generalInfo: {
                    projectNumber: selectedJobDetails?.BU_ID || selectedJob,
                    projectName: selectedJobDetails?.BU_Description || '',
                    client: selectedJobDetails?.AR_AB_Name || '',
                    location: selectedJobDetails?.Address_State_Name || '',
                    projectManager: selectedJobDetails?.Address_2_AB_Name || '',
                    contractType: selectedJobDetails?.Cat_Code_BU_08_Description || '',
                    plannedStartDate: selectedJobDetails?.Planned_Start_Date || '',
                    plannedCompleteDate: selectedJobDetails?.Planned_Complete_Date || '',
                    actualStartDate: selectedJobDetails?.Actual_Start_Date || '',
                },
                safetyData: safetyData,
                progressData: progressData,
                projectStatus: projectStatusNarrative,
                accomplishments: accomplishments,
                plannedActivities: plannedActivities,
                opportunities: opportunities,
                additionalComments: additionalComments,
                cashOutVariances: cashOutVariances,
                budgetRows: budgetRows.map(row => {
                    const getDataKey = (row) => {
                        if (row.label === 'Contract Amount') return 'Contract Amount';
                        if (row.label === 'Total Costs') return 'Total Costs';
                        if (row.label === 'Contingency') return 'Contingency';
                        if (row.costCode) return `costCode_${row.costCode}`;
                        if (row.discipline && budgetData[row.discipline]) return row.discipline;
                        return null;
                    };
                    const dataKey = getDataKey(row);
                    const rowData = dataKey ? budgetData[dataKey] : null;
                    return {
                        label: row.label,
                        level: row.level,
                        ...rowData
                    };
                }),
                risks: risks,
                issues: issues,
                milestones: milestones
            };

            // Call Azure Function
            const response = await fetch('https://forecastingtestpythonservice.azurewebsites.net/api/download_overview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(overviewData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Get the blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Overview_${selectedJob}_${versionInfo?.Version_Name || 'version'}_${moment().format('YYYYMMDD')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error exporting overview:', error);
            alert('Error exporting overview to Excel. Please try again.');
        }
    };

    // Build manpower chart data from Phase Hours in jobUserData
    useEffect(() => {
        if (!jobUserData || !selectedVersion || !selectedJob) return;
        
        // Collect phase hours data from jobUserData (Phase Hours tab)
        const phaseHoursData = {};
        const weekSet = new Set();
        
        jobUserData.forEach(item => {
            if (item.Forecast_Tab === 'Phase Hours' &&
                item.Version_GUID === selectedVersion &&
                item.BU_ID === selectedJob &&
                item.Forecast_Field_Name?.startsWith('week_')) {
                const week = item.Forecast_Field_Name.replace('week_', '');
                const phase = item.Forecast_Row_ID;
                weekSet.add(week);
                if (!phaseHoursData[week]) phaseHoursData[week] = {};
                phaseHoursData[week][phase] = (phaseHoursData[week][phase] || 0) + (parseFloat(item.Forecast_Value) || 0);
            }
        });
        
        // Also pull actual hours from jobCostDetail grouped by week
        // Note: overview uses jobCostDetailCondensed which only has GL_Date (not Consolidated_Date)
        // Align to isoWeek (Monday) start to match Phase Hours week keys
        const actualByWeek = {};
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(cd => {
                if (cd.Cost_Type_Code >= '51000' && cd.Cost_Type_Code <= '51799') {
                    const dateField = cd.GL_Date;
                    if (dateField) {
                        const weekStart = moment(dateField).startOf('isoWeek').format('YYYY-MM-DD');
                        actualByWeek[weekStart] = (actualByWeek[weekStart] || 0) + (parseFloat(cd.Total_Hours) || 0);
                        weekSet.add(weekStart);
                    }
                }
            });
        }
        
        // Build sorted week array and chart data
        const sortedWeeks = Array.from(weekSet).sort();
        
        // Show weeks around the current period: 8 weeks back + 4 weeks forward from current period
        const currentPeriod = versionInfo?.Version_Start_DateTime 
            ? moment(versionInfo.Version_Start_DateTime).startOf('isoWeek').format('YYYY-MM-DD')
            : moment().startOf('isoWeek').format('YYYY-MM-DD');
        
        // Find the index of the current period week (or closest)
        let currentIdx = sortedWeeks.findIndex(w => w >= currentPeriod);
        if (currentIdx === -1) currentIdx = sortedWeeks.length - 1;
        
        // Take 8 weeks before and 4 weeks after current for a 12-week window
        const startIdx = Math.max(0, currentIdx - 8);
        const endIdx = Math.min(sortedWeeks.length, startIdx + 12);
        const recentWeeks = sortedWeeks.slice(startIdx, endIdx);
        
        const chartData = recentWeeks.map(week => {
            // Sum planned hours from phase hours
            const planned = phaseHoursData[week] 
                ? Object.values(phaseHoursData[week]).reduce((sum, hrs) => sum + hrs, 0) 
                : 0;
            const actual = actualByWeek[week] || 0;
            return {
                week: moment(week).format('M/D'),
                planned: Math.round(planned),
                actual: Math.round(actual),
                rawWeek: week
            };
        });
        
        setManpowerChartData(chartData);
    }, [jobUserData, jobCostDetail, selectedVersion, selectedJob, versionInfo]);

    // Calculate planned hours per discipline from Phase Hours (labor graphs weekly breakout)
    const phaseHoursPlanned = useMemo(() => {
        if (!jobUserData || !selectedVersion || !budgetRows) return {};
        
        // Build Phase Hours data: { costCode: { weeks: { weekDate: hours } } }
        const phaseHoursData = {};
        jobUserData.forEach(item => {
            if (item.Forecast_Tab === 'Phase Hours' &&
                item.Version_GUID === selectedVersion &&
                item.BU_ID === selectedJob &&
                item.Forecast_Field_Name?.startsWith('week_')) {
                const week = item.Forecast_Field_Name.replace('week_', '');
                const costCode = item.Forecast_Row_ID;
                if (!phaseHoursData[costCode]) phaseHoursData[costCode] = {};
                phaseHoursData[costCode][week] = (phaseHoursData[costCode][week] || 0) + (parseFloat(item.Forecast_Value) || 0);
            }
        });
        
        // Also get the overall planned hours per week from Labor Graphs tab
        const laborGraphsPlanned = {};
        jobUserData.forEach(item => {
            if (item.Forecast_Tab === 'Labor Graphs' &&
                item.Version_GUID === selectedVersion &&
                item.BU_ID === selectedJob &&
                item.Forecast_Field_Name === 'plannedHours') {
                laborGraphsPlanned[item.Forecast_Row_ID] = parseFloat(item.Forecast_Value) || 0;
            }
        });
        
        // Current version week
        const currentWeek = versionInfo?.Version_Start_DateTime
            ? moment(versionInfo.Version_Start_DateTime).startOf('isoWeek').format('YYYY-MM-DD')
            : null;
        
        // Collect all weeks across all phase hours
        const allWeeks = new Set();
        Object.values(phaseHoursData).forEach(weeks => {
            Object.keys(weeks).forEach(w => allWeeks.add(w));
        });
        const sortedWeeks = Array.from(allWeeks).sort();
        
        // Build per-discipline data
        const result = {};
        const disciplines = budgetRows.filter(row => row.level === 0 && row.discipline && row.discipline !== 'Contingency' && row.label !== 'Contract Amount' && row.label !== 'Total Costs');
        
        disciplines.forEach(discRow => {
            const costCodes = budgetRows
                .filter(r => r.discipline === discRow.discipline && r.costCode)
                .map(r => r.costCode);
            
            let periodPlan = 0;
            let cumulativePlan = 0;
            
            costCodes.forEach(code => {
                const codeWeeks = phaseHoursData[code] || {};
                
                // Period plan: hours for the current version week
                if (currentWeek && codeWeeks[currentWeek]) {
                    periodPlan += codeWeeks[currentWeek];
                }
                
                // Cumulative plan: sum of hours for all weeks up to and including the current version week
                sortedWeeks.forEach(week => {
                    if (!currentWeek || week <= currentWeek) {
                        cumulativePlan += (codeWeeks[week] || 0);
                    }
                });
            });
            
            // Also compute total budget hours for this discipline to calculate percentage
            let totalBudgetHours = 0;
            costCodes.forEach(code => {
                const codeWeeks = phaseHoursData[code] || {};
                Object.values(codeWeeks).forEach(hrs => {
                    totalBudgetHours += hrs;
                });
            });
            
            result[discRow.discipline] = {
                periodPlan,
                cumulativePlan,
                totalBudgetHours,
                periodPlanPct: totalBudgetHours > 0 ? ((periodPlan / totalBudgetHours) * 100).toFixed(1) + '%' : '0.0%',
                cumulativePlanPct: totalBudgetHours > 0 ? ((cumulativePlan / totalBudgetHours) * 100).toFixed(1) + '%' : '0.0%'
            };
        });
        
        // Overall totals
        let overallPeriod = 0;
        let overallCumulative = 0;
        let overallTotal = 0;
        Object.values(result).forEach(d => {
            overallPeriod += d.periodPlan;
            overallCumulative += d.cumulativePlan;
            overallTotal += d.totalBudgetHours;
        });
        result['__overall'] = {
            periodPlan: overallPeriod,
            cumulativePlan: overallCumulative,
            totalBudgetHours: overallTotal,
            periodPlanPct: overallTotal > 0 ? ((overallPeriod / overallTotal) * 100).toFixed(1) + '%' : '0.0%',
            cumulativePlanPct: overallTotal > 0 ? ((overallCumulative / overallTotal) * 100).toFixed(1) + '%' : '0.0%'
        };
        
        return result;
    }, [jobUserData, selectedVersion, selectedJob, budgetRows, versionInfo]);

    // Quantity tracking data computed from same sources as Productivity Combined
    const quantityTrackingData = useMemo(() => {
        if (!jobUserData || !selectedVersion || !budgetRows) return {};

        const result = {};

        // Get discipline rows (same filter as Progress section)
        const disciplines = budgetRows.filter(row =>
            row.level === 0 && row.discipline && row.discipline !== 'Contingency' &&
            row.label !== 'Contract Amount' && row.label !== 'Total Costs'
        );

        let overallPlanned = 0;
        let overallActual = 0;

        disciplines.forEach(discRow => {
            const disciplineCostCodes = budgetRows
                .filter(r => r.discipline === discRow.discipline && r.costCode)
                .map(r => r.costCode);

            let discPlanned = 0;
            let discActual = 0;
            let discUom = '';
            const costCodeDetails = [];

            disciplineCostCodes.forEach(costCode => {
                // Day Zero Budget: Quantity and UoM
                const dayZeroItems = jobUserData.filter(item =>
                    item.Forecast_Tab === 'Day Zero Budget' &&
                    item.Cost_Code === costCode &&
                    item.Version_GUID === selectedVersion
                );
                const dayZeroQty = dayZeroItems.reduce((sum, item) => sum + (parseFloat(item?.Quantity) || 0), 0);
                const dayZeroUom = dayZeroItems.find(item => item?.UoM)?.UoM || '';

                // Change Orders: Quantity
                const coItems = jobUserData.filter(item =>
                    item.Forecast_Tab === 'Change Order' &&
                    item.Cost_Code === costCode &&
                    item.Version_GUID === selectedVersion &&
                    item.Status === 'Approved'
                );
                const coQty = coItems.reduce((sum, item) => sum + (parseFloat(item?.Quantity) || 0), 0);

                // Scorecard: plug quantity
                const plugItem = jobUserData.find(item =>
                    item.Forecast_Tab === 'Scorecard' &&
                    item.Forecast_Field_Name === 'plug' &&
                    item.Forecast_Row_ID === costCode &&
                    item.Version_GUID === selectedVersion
                );
                const plugQty = parseFloat(plugItem?.Forecast_Value) || 0;

                // Scorecard: installed (actual) quantity
                const installedItem = jobUserData.find(item =>
                    item.Forecast_Tab === 'Scorecard' &&
                    item.Forecast_Field_Name === 'installed' &&
                    item.Forecast_Row_ID === costCode &&
                    item.Version_GUID === selectedVersion
                );
                const installed = parseFloat(installedItem?.Forecast_Value) || 0;

                const totalPlanned = dayZeroQty + coQty + plugQty;

                if (totalPlanned > 0 || installed > 0) {
                    costCodeDetails.push({
                        costCode,
                        uom: dayZeroUom,
                        planned: totalPlanned,
                        actual: installed,
                        dayZeroQty,
                        coQty,
                        plugQty,
                    });
                }

                discPlanned += totalPlanned;
                discActual += installed;
                if (!discUom && dayZeroUom) discUom = dayZeroUom;
            });

            overallPlanned += discPlanned;
            overallActual += discActual;

            result[discRow.discipline] = {
                uom: discUom,
                planned: discPlanned,
                actual: discActual,
                variance: discPlanned - discActual,
                pctComplete: discPlanned > 0 ? ((discActual / discPlanned) * 100).toFixed(1) : '0.0',
                costCodes: costCodeDetails,
            };
        });

        result['__overall'] = {
            planned: overallPlanned,
            actual: overallActual,
            variance: overallPlanned - overallActual,
            pctComplete: overallPlanned > 0 ? ((overallActual / overallPlanned) * 100).toFixed(1) : '0.0',
        };

        return result;
    }, [jobUserData, selectedVersion, budgetRows]);

    const handlePrint = () => {
        window.print();
    };

    return (
        <>
            <style>
                {`
                    @media print {
                        .cost-code-row {
                            display: none !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                    }
                `}
            </style>
            <div style={containerStyle}>
            {/* HEADER / GENERAL INFO */}
            <div style={{ borderBottom: "1px solid #999", padding: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: "bold", fontSize: 14 }}>
                        {/* Replace with real logo img if you have one */}
                        STRIKE
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={exportOverviewToExcel}
                            title="Export to Excel"
                            style={{
                                padding: '6px 10px',
                                backgroundColor: 'transparent',
                                color: '#666',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                                e.currentTarget.style.borderColor = '#999';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#ddd';
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                            </svg>
                        </button>
                        <button
                            onClick={handlePrint}
                            title="Print Overview"
                            style={{
                                padding: '6px 10px',
                                backgroundColor: 'transparent',
                                color: '#666',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                                e.currentTarget.style.borderColor = '#999';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#ddd';
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z"/>
                            </svg>
                        </button>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div>{selectedJobDetails?.BU_Description || ''}</div>
                        <div>Weekly Report</div>
                        <div>{versionInfo?.Version_Start_DateTime ? new Date(versionInfo.Version_Start_DateTime).toLocaleDateString() : ''}</div>
                    </div>
                </div>

                <div style={{ marginTop: 8 }}>
                    <div style={{ ...sectionHeaderStyle, borderTop: "none" }}>
                        1&nbsp; General Information
                    </div>
                    <table style={tableStyle}>
                        <tbody>
                            <tr>
                                <td style={tdStyleHeader}>Project Number</td>
                                <td style={tdStyle}>{selectedJobDetails?.BU_ID || selectedJob}</td>
                                <td style={tdStyleHeader}>Accountable Person</td>
                                <td style={tdStyle}>{selectedJobDetails?.Project_Manager_Name || ''}</td>
                            </tr>
                            <tr>
                                <td style={tdStyleHeader}>Client</td>
                                <td style={tdStyle}>{selectedJobDetails?.AR_AB_Name || ''}</td>
                                <td style={tdStyleHeader}>Project Location</td>
                                <td style={tdStyle}>{selectedJobDetails?.Address_State_Name || ''}</td>
                            </tr>
                            <tr>
                                <td style={tdStyleHeader}>Team</td>
                                <td style={tdStyle}>{selectedJobDetails?.Cat_Code_BU_27 || ''}</td>
                                <td style={tdStyleHeader}>Contract Type</td>
                                <td style={tdStyle}>{selectedJobDetails?.Cat_Code_BU_08_Description || ''}</td>
                            </tr>
                            <tr>
                                <td style={tdStyleHeader}>Project Name</td>
                                <td style={{ ...tdStyle }} colSpan={3}>
                                    {selectedJobDetails?.BU_Description || ''}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SAFETY + OVERALL STATUS */}
            <div>
                <table style={tableStyle}>
                    <tbody>
                        <tr>
                            <td style={{ ...sectionHeaderStyle, width: "55%" }}>
                                2&nbsp; Safety Indicators (add leading indicator data)
                            </td>
                            <td style={{ ...sectionHeaderStyle, width: "45%" }}>
                                3&nbsp; Overall Project Status
                            </td>
                        </tr>
                        <tr>
                            {/* Safety table */}
                            <td style={tdStyle}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Crew</th>
                                            <th style={thStyle}>Hours</th>
                                            <th style={thStyle}>First Aid</th>
                                            <th style={thStyle}>Incidents</th>
                                            <th style={thStyle}>Near Miss</th>
                                            <th style={thStyle}>Stop Work</th>
                                            <th style={thStyle}>Weather Impacts</th>
                                            <th style={thStyle}>QAQC</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Strike row - always visible */}
                                        <tr>
                                            <td style={tdStyle}>Strike</td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-hours')}>
                                                {editingSafetyCell === 'strike-hours' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.hours}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'hours', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'hours', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.hours}
                                            </td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-firstAid')}>
                                                {editingSafetyCell === 'strike-firstAid' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.firstAid}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'firstAid', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'firstAid', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.firstAid}
                                            </td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-nearMiss')}>
                                                {editingSafetyCell === 'strike-nearMiss' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.nearMiss}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'nearMiss', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'nearMiss', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.nearMiss}
                                            </td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-ltas')}>
                                                {editingSafetyCell === 'strike-ltas' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.ltas}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'ltas', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'ltas', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.ltas}
                                            </td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-weatherImpacts')}>
                                                {editingSafetyCell === 'strike-weatherImpacts' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.weatherImpacts}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'weatherImpacts', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'weatherImpacts', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.weatherImpacts}
                                            </td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-incidentRates')}>
                                                {editingSafetyCell === 'strike-incidentRates' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.incidentRates}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'incidentRates', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'incidentRates', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.incidentRates}
                                            </td>
                                            <td style={centeredCell} onClick={() => setEditingSafetyCell('strike-qaqc')}>
                                                {editingSafetyCell === 'strike-qaqc' ? (
                                                    <input
                                                        type="number"
                                                        defaultValue={safetyData.strike.qaqc}
                                                        onBlur={(e) => handleSafetyCellChange('strike', 'qaqc', e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSafetyCellChange('strike', 'qaqc', e.target.value)}
                                                        autoFocus
                                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                    />
                                                ) : safetyData.strike.qaqc || 0}
                                            </td>
                                        </tr>
                                        
                                        {/* Dynamic sub rows */}
                                        {safetyData.subs.map((sub) => (
                                            <tr key={sub.id}>
                                                <td style={tdStyle}>
                                                    {editingSubId === sub.id ? (
                                                        <input
                                                            type="text"
                                                            defaultValue={sub.name}
                                                            onBlur={(e) => handleSaveSubName(sub.id, e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSaveSubName(sub.id, e.target.value)}
                                                            autoFocus
                                                            placeholder="Enter sub name..."
                                                            style={{ width: '100%', padding: '4px', border: '1px solid #2d6cdf' }}
                                                        />
                                                    ) : (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span>{sub.name}</span>
                                                            <button
                                                                onClick={() => handleDeleteSub(sub.id)}
                                                                style={{
                                                                    padding: '2px 6px',
                                                                    fontSize: '10px',
                                                                    cursor: 'pointer',
                                                                    backgroundColor: '#f44336',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '3px',
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-hours`)}>
                                                    {editingSafetyCell === `${sub.id}-hours` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.hours}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'hours', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'hours', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.hours}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-firstAid`)}>
                                                    {editingSafetyCell === `${sub.id}-firstAid` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.firstAid}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'firstAid', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'firstAid', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.firstAid}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-nearMiss`)}>
                                                    {editingSafetyCell === `${sub.id}-nearMiss` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.nearMiss}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'nearMiss', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'nearMiss', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.nearMiss}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-ltas`)}>
                                                    {editingSafetyCell === `${sub.id}-ltas` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.ltas}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'ltas', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'ltas', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.ltas}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-weatherImpacts`)}>
                                                    {editingSafetyCell === `${sub.id}-weatherImpacts` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.weatherImpacts}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'weatherImpacts', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'weatherImpacts', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.weatherImpacts}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-incidentRates`)}>
                                                    {editingSafetyCell === `${sub.id}-incidentRates` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.incidentRates}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'incidentRates', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'incidentRates', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.incidentRates}
                                                </td>
                                                <td style={centeredCell} onClick={() => setEditingSafetyCell(`${sub.id}-qaqc`)}>
                                                    {editingSafetyCell === `${sub.id}-qaqc` ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={sub.qaqc}
                                                            onBlur={(e) => handleSubCellChange(sub.id, 'qaqc', e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSubCellChange(sub.id, 'qaqc', e.target.value)}
                                                            autoFocus
                                                            style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                        />
                                                    ) : sub.qaqc || 0}
                                                </td>
                                            </tr>
                                        ))}
                                        
                                        {/* Add sub button row */}
                                        <tr>
                                            <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', padding: '8px' }}>
                                                <button
                                                    onClick={handleAddSub}
                                                    style={{
                                                        padding: '6px 12px',
                                                        fontSize: '11px',
                                                        cursor: 'pointer',
                                                        backgroundColor: '#4CAF50',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                    }}
                                                >
                                                    + Add Sub
                                                </button>
                                            </td>
                                        </tr>
                                        
                                        {/* TOTAL row - always visible */}
                                        <tr>
                                            <td style={{ ...tdStyle, fontWeight: "bold" }}>TOTAL</td>
                                            <td style={centeredCell}>{getSafetyTotal('hours')}</td>
                                            <td style={centeredCell}>{getSafetyTotal('firstAid')}</td>
                                            <td style={centeredCell}>{getSafetyTotal('nearMiss')}</td>
                                            <td style={centeredCell}>{getSafetyTotal('ltas')}</td>
                                            <td style={centeredCell}>{getSafetyTotal('weatherImpacts')}</td>
                                            <td style={centeredCell}>{getSafetyTotal('incidentRates')}</td>
                                            <td style={centeredCell}>{getSafetyTotal('qaqc')}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>

                            {/* Overall status */}
                            <td style={tdStyle}>
                                {renderStatusPopup()}
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Overall<InfoIcon onClick={(e) => handleStatusPopup(e, 'overall')} /></th>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Productivity<InfoIcon onClick={(e) => handleStatusPopup(e, 'productivity')} /></th>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Cost<InfoIcon onClick={(e) => handleStatusPopup(e, 'cost')} /></th>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Risk<InfoIcon onClick={(e) => handleStatusPopup(e, 'risk')} /></th>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Schedule<InfoIcon onClick={(e) => handleStatusPopup(e, 'schedule')} /></th>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Billing<InfoIcon onClick={(e) => handleStatusPopup(e, 'billing')} /></th>
                                            <th style={{ ...thStyle, width: '14.28%' }}>Safety<InfoIcon onClick={(e) => handleStatusPopup(e, 'safety')} /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getOverallStatus()}</td>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getProductivityStatus()}</td>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getBudgetStatus()}</td>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getRiskStatus()}</td>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getScheduleStatus()}</td>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getBillingStatus()}</td>
                                            <td style={{ ...centeredCell, width: '14.28%' }}>{getSafetyStatus()}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ ...tdStyle }} colSpan={7}>
                                                {isEditingNarrative ? (
                                                    <div>
                                                        <textarea
                                                            value={projectStatusNarrative}
                                                            onChange={(e) => setProjectStatusNarrative(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                minHeight: '80px',
                                                                padding: '8px',
                                                                fontSize: '11px',
                                                                fontFamily: 'Arial, sans-serif',
                                                                border: '1px solid #999',
                                                                borderRadius: '4px',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                                            <button
                                                                onClick={handleSaveNarrative}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: '#2d6cdf',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '11px',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => setIsEditingNarrative(false)}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: '#f2f2f2',
                                                                    color: '#000',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '11px'
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        onClick={() => setIsEditingNarrative(true)}
                                                        style={{ cursor: 'pointer', minHeight: '60px' }}
                                                        title="Click to edit"
                                                    >
                                                        {projectStatusNarrative || 'Click to add project status narrative...'}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* PROGRESS & SCHEDULE */}
            <div>
                <div style={sectionHeaderStyle}>4&nbsp; Progress and Schedule</div>


                {/* Construction Percent Complete For */}
                {renderMathPopup()}
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle} rowSpan={2}>
                                Construction Percent Complete For
                            </th>
                            <th style={thStyle} colSpan={periodExpanded ? 5 : 0} onClick={() => setPeriodExpanded(!periodExpanded)}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer' }}>
                                    <span>Period</span>
                                    <span style={{ fontSize: '10px', color: '#2d6cdf', fontWeight: 'bold' }}>{periodExpanded ? '▼' : '▶'}</span>
                                </div>
                            </th>
                            <th style={thStyle} colSpan={4}>
                                Cumulative
                            </th>
                        </tr>
                        <tr>
                            {periodExpanded && <>
                                <th style={thStyle}>Plan<InfoIcon onClick={(e) => handleMathPopup(e, 'periodPlan', '__header', null)} /></th>
                                <th style={thStyle}>Actual<InfoIcon onClick={(e) => handleMathPopup(e, 'periodActual', '__header', null)} /></th>
                                <th style={thStyle}>Fcast<InfoIcon onClick={(e) => handleMathPopup(e, 'periodFcast', '__header', null)} /></th>
                                <th style={thStyle}>Δ %<InfoIcon onClick={(e) => handleMathPopup(e, 'periodDeltaPercent', '__header', null)} /></th>
                                <th style={thStyle}>Δ $<InfoIcon onClick={(e) => handleMathPopup(e, 'periodDeltaDollar', '__header', null)} /></th>
                            </>}
                            <th style={thStyle}>Plan<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativePlan', '__header', null)} /></th>
                            <th style={thStyle}>Actual<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeActual', '__header', null)} /></th>
                            <th style={thStyle}>Fcast<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeFcast', '__header', null)} /></th>
                            <th style={thStyle}>Δ % Plan<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeDeltaPlan', '__header', null)} /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const filteredRows = budgetRows.filter(row => row.level === 0 && row.discipline && row.discipline !== 'Contingency' && row.label !== 'Contract Amount' && row.label !== 'Total Costs');
                            console.log('Filtered discipline rows for Progress section:', filteredRows);
                            return filteredRows;
                        })()
                            .map((row) => {
                                // Get all cost codes (phases) for this discipline
                                const disciplineCostCodes = budgetRows
                                    .filter(r => r.discipline === row.discipline && r.costCode)
                                    .map(r => r.costCode);
                                
                                console.log(`Processing discipline: ${row.discipline}, cost codes:`, disciplineCostCodes);
                                
                                // Initialize cumulative values for discipline
                                let disciplineBudgetHours = 0;
                                let disciplineActualHours = 0;
                                let disciplineForecastHours = 0;
                                let disciplineTrendingEACHours = 0;
                                let disciplineInstalledQty = 0;
                                let disciplineTotalQty = 0;
                                
                                // Calculate for each phase (cost code) in the discipline
                                disciplineCostCodes.forEach(costCode => {
                                    // Get installed quantity from Scorecard
                                    const installedItem = jobUserData?.find(item =>
                                        item.Forecast_Tab === 'Scorecard' &&
                                        item.Forecast_Field_Name === 'installed' &&
                                        item.Forecast_Row_ID === costCode &&
                                        item.Version_GUID === selectedVersion
                                    );
                                    
                                    // Get day zero data (multiple rows possible)
                                    const dayZeroItems = jobUserData?.filter(item =>
                                        item.Forecast_Tab === 'Day Zero Budget' &&
                                        item.Cost_Code === costCode &&
                                        item.Version_GUID === selectedVersion
                                    ) || [];
                                    
                                    // Get CO data (multiple rows possible)
                                    const coItems = jobUserData?.filter(item =>
                                        item.Forecast_Tab === 'Change Order' &&
                                        item.Cost_Code === costCode &&
                                        item.Version_GUID === selectedVersion
                                    ) || [];

                                    console.log('day zero and co ', dayZeroItems, coItems);
                                    
                                    // Get plug quantity
                                    const plugItem = jobUserData?.find(item =>
                                        item.Forecast_Tab === 'Scorecard' &&
                                        item.Forecast_Field_Name === 'plug' &&
                                        item.Forecast_Row_ID === costCode &&
                                        item.Version_GUID === selectedVersion
                                    );
                                    
                                    // Get forecasted hours
                                    const forecastedHoursItem = jobUserData?.find(item =>
                                        item.Forecast_Tab === 'Scorecard' &&
                                        item.Forecast_Field_Name === 'forecastedHrs' &&
                                        item.Forecast_Row_ID === costCode &&
                                        item.Version_GUID === selectedVersion
                                    );
                                    
                                    const installed = parseFloat(installedItem?.Forecast_Value) || 0;
                                    
                                    // Sum up quantities and hours from all day zero rows
                                    const dayZeroQty = dayZeroItems.reduce((sum, item) => sum + (parseFloat(item?.Quantity) || 0), 0);
                                    const dayZeroHours = dayZeroItems.reduce((sum, item) => sum + (parseFloat(item?.Hours) || 0), 0);
                                    
                                    // Sum up quantities and hours from all CO rows
                                    const coQty = coItems.reduce((sum, item) => sum + (parseFloat(item?.Quantity) || 0), 0);
                                    const coHours = coItems.reduce((sum, item) => sum + (parseFloat(item?.Hours) || 0), 0);
                                    const plugQty = parseFloat(plugItem?.Forecast_Value) || 0;
                                    const totalQty = dayZeroQty + coQty + plugQty;
                                    const budgetHours = dayZeroHours + coHours;
                                    
                                    // Get actual hours from cost detail for this phase
                                    let phaseActualHours = 0;
                                    const costDetailRows = jobCostDetail?.filter(cd => 
                                        cd.Phase_Code === costCode &&
                                        cd.Cost_Type_Code &&
                                        cd.Cost_Type_Code[0] === '5' &&
                                        cd.Cost_Type_Code[1] === '1' &&
                                        cd.Cost_Type_Code[2] !== '8'
                                    ) || [];
                                    costDetailRows.forEach(cd => {
                                        phaseActualHours += parseFloat(cd.Total_Hours) || 0;
                                    });

                                    
                                    // Calculate percent complete for this phase
                                    const phasePercentComplete = totalQty > 0 ? (installed / totalQty) * 100 : 0;
                                    
                                    // Calculate actual hours/qty (unit rate)
                                    const actualHoursQty = installed && installed > 0 
                                        ? (phasePercentComplete < 25 
                                            ? (budgetHours / totalQty) 
                                            : (phaseActualHours / installed))
                                        : 0;
                                    
                                    // Calculate trending EAC hours for this phase
                                    const phaseTrendingEAC = phasePercentComplete > 25
                                        ? actualHoursQty * totalQty
                                        : (phaseActualHours > budgetHours ? phaseActualHours : budgetHours);
                                    
                                    // Get forecasted hours (ETC from Productivity Tracker)
                                    const phaseForecastedHours = parseFloat(forecastedHoursItem?.Forecast_Value) || 0;
                                    
                                    // Calculate PM EAC (forecast + actual)
                                    const phasePMEAC = phaseForecastedHours + phaseActualHours;
                                    
                                    // Accumulate to discipline totals
                                    disciplineBudgetHours += budgetHours;
                                    disciplineActualHours += phaseActualHours;
                                    disciplineForecastHours += phasePMEAC;
                                    disciplineTrendingEACHours += phaseTrendingEAC;
                                    disciplineInstalledQty += installed;
                                    disciplineTotalQty += totalQty;
                                });
                                
                                console.log('Totals for discipline', row.discipline, disciplineTotalQty, disciplineInstalledQty)
                                // Calculate discipline-level percentages
                                const disciplinePlanPercent = disciplineTotalQty > 0 
                                    ? ((disciplineInstalledQty / disciplineTotalQty) * 100).toFixed(1) + '%'
                                    : '0.0%';
                                
                                const disciplineActualPercent = disciplineBudgetHours > 0
                                    ? ((disciplineActualHours / disciplineBudgetHours) * 100).toFixed(1) + '%'
                                    : '0.0%';
                                
                                const disciplineForecastPercent = disciplineBudgetHours > 0
                                    ? ((disciplineForecastHours / disciplineBudgetHours) * 100).toFixed(1) + '%'
                                    : '0.0%';
                                
                                const disciplineTrendingPercent = disciplineBudgetHours > 0
                                    ? ((disciplineTrendingEACHours / disciplineBudgetHours) * 100).toFixed(1) + '%'
                                    : '0.0%';
                                
                                const disciplineDeltaPlan = disciplineBudgetHours > 0
                                    ? ((disciplineActualHours - disciplineBudgetHours) / disciplineBudgetHours * 100).toFixed(1) + '%'
                                    : '0.0%';
                                
                                // Get Phase Hours planned data for this discipline
                                const phPlanned = phaseHoursPlanned[row.discipline] || {};
                                const periodPlanFromLG = phPlanned.periodPlanPct || '0.0%';
                                const cumulativePlanFromLG = phPlanned.cumulativePlanPct || '0.0%';
                                
                                // Bundle calc data for math popup
                                const discCalcData = {
                                    ...phPlanned,
                                    actualHours: disciplineActualHours,
                                    budgetHours: disciplineBudgetHours,
                                    forecastHours: disciplineForecastHours,
                                    trendingHours: disciplineTrendingEACHours,
                                    actualPercent: disciplineActualPercent,
                                    forecastPercent: disciplineForecastPercent,
                                    trendingPercent: disciplineTrendingPercent,
                                    deltaPlan: disciplineDeltaPlan,
                                };
                                
                                return (
                                    <tr key={row.discipline}>
                                        <td style={tdStyle}>{row.discipline}</td>
                                        {periodExpanded && <>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_periodPlan`)}>
                                            {editingProgressCell === `${row.discipline}_periodPlan` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressPeriodPlan', periodPlanFromLG)}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressPeriodPlan', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressPeriodPlan', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressPeriodPlan', periodPlanFromLG)}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodPlan', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_periodActual`)}>
                                            {editingProgressCell === `${row.discipline}_periodActual` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressPeriodActual', '0.0%')}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressPeriodActual', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressPeriodActual', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressPeriodActual', '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodActual', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_periodFcast`)}>
                                            {editingProgressCell === `${row.discipline}_periodFcast` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressPeriodFcast', '0.0%')}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressPeriodFcast', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressPeriodFcast', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressPeriodFcast', '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodFcast', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_periodDeltaPercent`)}>
                                            {editingProgressCell === `${row.discipline}_periodDeltaPercent` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressPeriodDeltaPercent', '0.0%')}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressPeriodDeltaPercent', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressPeriodDeltaPercent', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressPeriodDeltaPercent', '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodDeltaPercent', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_periodDeltaDollar`)}>
                                            {editingProgressCell === `${row.discipline}_periodDeltaDollar` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressPeriodDeltaDollar', '0')}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressPeriodDeltaDollar', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressPeriodDeltaDollar', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressPeriodDeltaDollar', '0')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodDeltaDollar', row.discipline, discCalcData)} /></>}
                                        </td>
                                        </>}
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_cumulativePlan`)}>
                                            {editingProgressCell === `${row.discipline}_cumulativePlan` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressCumulativePlan', cumulativePlanFromLG)}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressCumulativePlan', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressCumulativePlan', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressCumulativePlan', cumulativePlanFromLG)}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativePlan', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_cumulativeActual`)}>
                                            {editingProgressCell === `${row.discipline}_cumulativeActual` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressCumulativeActual', disciplineActualPercent)}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressCumulativeActual', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressCumulativeActual', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressCumulativeActual', disciplineActualPercent)}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeActual', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_cumulativeFcast`)}>
                                            {editingProgressCell === `${row.discipline}_cumulativeFcast` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressCumulativeFcast', disciplineForecastPercent)}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressCumulativeFcast', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressCumulativeFcast', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressCumulativeFcast', disciplineForecastPercent)}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeFcast', row.discipline, discCalcData)} /></>}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingProgressCell(`${row.discipline}_cumulativeDeltaPlan`)}>
                                            {editingProgressCell === `${row.discipline}_cumulativeDeltaPlan` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={getProgressValue(row.discipline, 'ProgressCumulativeDeltaPlan', disciplineDeltaPlan)}
                                                    onBlur={(e) => handleProgressCellChange(row.discipline, 'ProgressCumulativeDeltaPlan', e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange(row.discipline, 'ProgressCumulativeDeltaPlan', e.target.value)}
                                                    autoFocus
                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                                />
                                            ) : <>{getProgressValue(row.discipline, 'ProgressCumulativeDeltaPlan', disciplineDeltaPlan)}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeDeltaPlan', row.discipline, discCalcData)} /></>}
                                        </td>
                                    </tr>
                                );
                            })}
                        {(() => {
                            const overallCalcData = phaseHoursPlanned['__overall'] || {};
                            return (
                        <tr>
                            <td style={{ ...tdStyle, fontWeight: "bold" }}>Overall Progress</td>
                            {periodExpanded && <>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_periodPlan')}>
                                {editingProgressCell === 'Overall_periodPlan' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressPeriodPlan', phaseHoursPlanned['__overall']?.periodPlanPct || '0.0%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressPeriodPlan', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressPeriodPlan', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressPeriodPlan', phaseHoursPlanned['__overall']?.periodPlanPct || '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodPlan', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_periodActual')}>
                                {editingProgressCell === 'Overall_periodActual' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressPeriodActual', '0.0%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressPeriodActual', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressPeriodActual', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressPeriodActual', '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodActual', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_periodFcast')}>
                                {editingProgressCell === 'Overall_periodFcast' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressPeriodFcast', '0.0%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressPeriodFcast', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressPeriodFcast', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressPeriodFcast', '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodFcast', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_periodDeltaPercent')}>
                                {editingProgressCell === 'Overall_periodDeltaPercent' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressPeriodDeltaPercent', '0.0%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressPeriodDeltaPercent', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressPeriodDeltaPercent', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressPeriodDeltaPercent', '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodDeltaPercent', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_periodDeltaDollar')}>
                                {editingProgressCell === 'Overall_periodDeltaDollar' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressPeriodDeltaDollar', '0')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressPeriodDeltaDollar', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressPeriodDeltaDollar', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressPeriodDeltaDollar', '0')}<InfoIcon onClick={(e) => handleMathPopup(e, 'periodDeltaDollar', 'Overall', overallCalcData)} /></>}
                            </td>
                            </>}
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_cumulativePlan')}>
                                {editingProgressCell === 'Overall_cumulativePlan' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressCumulativePlan', phaseHoursPlanned['__overall']?.cumulativePlanPct || '0.0%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressCumulativePlan', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressCumulativePlan', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressCumulativePlan', phaseHoursPlanned['__overall']?.cumulativePlanPct || '0.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativePlan', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_cumulativeActual')}>
                                {editingProgressCell === 'Overall_cumulativeActual' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressCumulativeActual', '89.2%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressCumulativeActual', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressCumulativeActual', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressCumulativeActual', '89.2%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeActual', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_cumulativeFcast')}>
                                {editingProgressCell === 'Overall_cumulativeFcast' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressCumulativeFcast', '89.2%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressCumulativeFcast', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressCumulativeFcast', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressCumulativeFcast', '89.2%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeFcast', 'Overall', overallCalcData)} /></>}
                            </td>
                            <td style={centeredCell} onClick={() => setEditingProgressCell('Overall_cumulativeDeltaPlan')}>
                                {editingProgressCell === 'Overall_cumulativeDeltaPlan' ? (
                                    <input
                                        type="text"
                                        defaultValue={getProgressValue('Overall', 'ProgressCumulativeDeltaPlan', '100.0%')}
                                        onBlur={(e) => handleProgressCellChange('Overall', 'ProgressCumulativeDeltaPlan', e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleProgressCellChange('Overall', 'ProgressCumulativeDeltaPlan', e.target.value)}
                                        autoFocus
                                        style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }}
                                    />
                                ) : <>{getProgressValue('Overall', 'ProgressCumulativeDeltaPlan', '100.0%')}<InfoIcon onClick={(e) => handleMathPopup(e, 'cumulativeDeltaPlan', 'Overall', overallCalcData)} /></>}
                            </td>
                        </tr>
                            );
                        })()}
                    </tbody>
                </table>

                {/* Key milestones */}
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Key Milestones</th>
                            <th style={thStyle}>Owner</th>
                            <th style={thStyle}>Current Baseline</th>
                            <th style={thStyle}>Actual / Forecast</th>
                            <th style={thStyle}>Prev F/C</th>
                            <th style={thStyle}>Remarks (Required if delta is more than a week)</th>
                            <th style={thStyle}>Weekly Δ</th>
                            <th style={thStyle}>Baseline Δ</th>
                            <th style={thStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {milestones.map((milestone) => (
                            <tr key={milestone.id}>
                                {editingMilestoneId === milestone.id ? (
                                    <>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingMilestone?.milestone || ''}
                                                onChange={(e) =>
                                                    setEditingMilestone({ ...editingMilestone, milestone: e.target.value })
                                                }
                                                style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingMilestone?.owner || ''}
                                                onChange={(e) =>
                                                    setEditingMilestone({ ...editingMilestone, owner: e.target.value })
                                                }
                                                style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="date"
                                                value={editingMilestone?.currentBaseline || ''}
                                                onChange={(e) =>
                                                    setEditingMilestone({ ...editingMilestone, currentBaseline: e.target.value })
                                                }
                                                style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="date"
                                                value={editingMilestone?.actualForecast || ''}
                                                onChange={(e) =>
                                                    setEditingMilestone({ ...editingMilestone, actualForecast: e.target.value })
                                                }
                                                style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="date"
                                                value={editingMilestone?.prevForecast || ''}
                                                onChange={(e) =>
                                                    setEditingMilestone({ ...editingMilestone, prevForecast: e.target.value })
                                                }
                                                style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <textarea
                                                value={editingMilestone?.remarks || ''}
                                                onChange={(e) =>
                                                    setEditingMilestone({ ...editingMilestone, remarks: e.target.value })
                                                }
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', minHeight: '60px' }}
                                            />
                                        </td>
                                        <td style={centeredCell}>
                                            <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>Auto-calculated</span>
                                        </td>
                                        <td style={centeredCell}>
                                            <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>Auto-calculated</span>
                                        </td>
                                        <td style={tdStyle}>
                                            <button
                                                onClick={handleSaveMilestone}
                                                style={{
                                                    marginRight: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    cursor: 'pointer',
                                                    backgroundColor: '#6c757d',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                }}
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={handleCancelEditMilestone}
                                                style={{
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    cursor: 'pointer',
                                                    backgroundColor: '#6c757d',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td style={tdStyle}>{milestone.milestone}</td>
                                        <td style={tdStyle}>{milestone.owner}</td>
                                        <td style={centeredCell}>{milestone.currentBaseline ? moment(milestone.currentBaseline).format('DD-MMM-YY') : ''}</td>
                                        <td style={centeredCell}>{milestone.actualForecast ? moment(milestone.actualForecast).format('DD-MMM-YY') : ''}</td>
                                        <td style={centeredCell}>{milestone.prevForecast ? moment(milestone.prevForecast).format('DD-MMM-YY') : ''}</td>
                                        <td style={tdStyle}>{milestone.remarks}</td>
                                        <td style={centeredCell}>{milestone.weeklyDelta}</td>
                                        <td style={centeredCell}>{milestone.baselineDelta}</td>
                                        <td style={tdStyle}>
                                            <button
                                                onClick={() => handleEditMilestone(milestone)}
                                                title="Edit"
                                                style={{
                                                    marginRight: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'transparent',
                                                    color: '#666',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '3px',
                                                }}
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDeleteMilestone(milestone.id)}
                                                title="Delete"
                                                style={{
                                                    padding: '4px 8px',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'transparent',
                                                    color: '#666',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '3px',
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>
                                <button
                                    onClick={handleAddMilestone}
                                    style={{
                                        padding: '6px 12px',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                    }}
                                >
                                    + Add Milestone
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Labor Graph (from laborGraphs.js - chart only) */}
                <div style={{ marginTop: '10px', border: '1px solid #999', overflow: 'auto' }}>
                    <LaborGraphs graphOnly={true} />
                </div>

                {/* S-Curve Chart */}
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff', border: '1px solid #999' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '8px', color: '#333' }}>S-Curve: Planned vs Actual Progress</div>
                    {(() => {
                        // Build S-curve data from phaseHoursPlanned
                        const overall = phaseHoursPlanned['__overall'] || {};
                        const months = overall.monthlyPlan || [];
                        const monthlyActual = overall.monthlyActual || [];
                        // If no monthly data, generate from cumulative plan/actual to show a simple curve
                        const dataPoints = [];
                        if (months.length > 0) {
                            let cumPlan = 0, cumActual = 0;
                            months.forEach((m, i) => {
                                cumPlan += (parseFloat(m) || 0);
                                cumActual += (parseFloat(monthlyActual[i]) || 0);
                                dataPoints.push({ month: i + 1, plan: cumPlan, actual: cumActual });
                            });
                        }
                        // If we have data, render SVG chart
                        if (dataPoints.length > 0) {
                            const maxVal = Math.max(...dataPoints.map(d => Math.max(d.plan, d.actual)), 1);
                            const w = 600, h = 200, pad = 40;
                            const xScale = (i) => pad + (i / (dataPoints.length - 1)) * (w - pad * 2);
                            const yScale = (v) => h - pad - (v / maxVal) * (h - pad * 2);
                            const planPath = dataPoints.map((d, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(d.plan)}`).join(' ');
                            const actualPath = dataPoints.map((d, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(d.actual)}`).join(' ');
                            return (
                                <svg width={w} height={h} style={{ display: 'block', margin: '0 auto' }}>
                                    {/* Grid lines */}
                                    {[0, 0.25, 0.5, 0.75, 1].map(pct => (
                                        <line key={pct} x1={pad} x2={w - pad} y1={yScale(maxVal * pct)} y2={yScale(maxVal * pct)} stroke="#eee" strokeWidth={1} />
                                    ))}
                                    {/* Axes */}
                                    <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#999" strokeWidth={1} />
                                    <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#999" strokeWidth={1} />
                                    {/* Plan line */}
                                    <path d={planPath} fill="none" stroke="#2d6cdf" strokeWidth={2} />
                                    {/* Actual line */}
                                    <path d={actualPath} fill="none" stroke="#28a745" strokeWidth={2} strokeDasharray="5,3" />
                                    {/* Legend */}
                                    <line x1={w - 180} y1={15} x2={w - 160} y2={15} stroke="#2d6cdf" strokeWidth={2} />
                                    <text x={w - 155} y={19} fontSize={10} fill="#333">Planned</text>
                                    <line x1={w - 100} y1={15} x2={w - 80} y2={15} stroke="#28a745" strokeWidth={2} strokeDasharray="5,3" />
                                    <text x={w - 75} y={19} fontSize={10} fill="#333">Actual</text>
                                    {/* X axis labels */}
                                    {dataPoints.filter((_, i) => i % Math.ceil(dataPoints.length / 8) === 0 || i === dataPoints.length - 1).map((d, i) => (
                                        <text key={i} x={xScale(dataPoints.indexOf(d))} y={h - pad + 14} fontSize={9} fill="#666" textAnchor="middle">M{d.month}</text>
                                    ))}
                                </svg>
                            );
                        }
                        // Fallback: placeholder message
                        return (
                            <div style={{ textAlign: 'center', color: '#999', fontSize: '11px', padding: '30px 0', fontStyle: 'italic' }}>
                                S-Curve chart will display when monthly plan/actual data is available in the progress data.
                            </div>
                        );
                    })()}
                </div>

                {/* Critical Path Notes */}
                <div style={{ padding: '6px', backgroundColor: '#fff9e6', border: '1px solid #999', borderTop: 'none' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px', color: '#666' }}>Schedule Notes</div>
                    {isEditingCriticalPathNotes ? (
                        <div>
                            <textarea
                                value={criticalPathNotes}
                                onChange={(e) => setCriticalPathNotes(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: '60px',
                                    padding: '8px',
                                    fontSize: '11px',
                                    fontFamily: 'Arial, sans-serif',
                                    border: '1px solid #999',
                                    borderRadius: '4px',
                                    resize: 'vertical'
                                }}
                                placeholder="Anything that affects critical path..."
                            />
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                <button onClick={handleSaveCriticalPathNotes} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                <button onClick={() => setIsEditingCriticalPathNotes(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div
                            onClick={() => setIsEditingCriticalPathNotes(true)}
                            style={{ cursor: 'pointer', minHeight: '40px', whiteSpace: 'pre-line', fontSize: '11px', color: criticalPathNotes ? '#000' : '#999', fontStyle: criticalPathNotes ? 'normal' : 'italic' }}
                            title="Click to edit"
                        >
                            {criticalPathNotes || 'Anything that affects critical path...'}
                        </div>
                    )}
                </div>
            </div>

            {/* QUANTITY TRACKING - moved under Progress & Schedule */}
            <div>
                <div style={sectionHeaderStyle}>5&nbsp; Quantity Tracking (Planned vs Actual)</div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Discipline</th>
                            <th style={thStyle}>UoM</th>
                            <th style={thStyle}>Planned Qty</th>
                            <th style={thStyle}>Actual Qty</th>
                            <th style={thStyle}>Variance</th>
                            <th style={thStyle}>% Complete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {budgetRows.filter(row => row.level === 0 && row.discipline && row.discipline !== 'Contingency' && row.label !== 'Contract Amount' && row.label !== 'Total Costs')
                            .map((row) => {
                                const qData = quantityTrackingData[row.discipline] || {};
                                const planned = qData.planned || 0;
                                const actual = qData.actual || 0;
                                const computedUom = qData.uom || '';
                                const uomOverride = getQuantityValue(row.discipline, 'QuantityUnit');
                                const unit = uomOverride || computedUom;
                                const variance = planned - actual;
                                const pctComplete = qData.pctComplete || '0.0';
                                return (
                                    <tr key={row.discipline}>
                                        <td style={tdStyle}>{row.discipline}</td>
                                        <td style={centeredCell} onClick={() => setEditingQuantityCell(`${row.discipline}_unit`)}>
                                            {editingQuantityCell === `${row.discipline}_unit` ? (
                                                <input type="text" defaultValue={unit} onBlur={(e) => handleQuantityCellChange(row.discipline, 'QuantityUnit', e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleQuantityCellChange(row.discipline, 'QuantityUnit', e.target.value)} autoFocus style={{ width: '100%', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px' }} placeholder="e.g. LF, EA, CY" />
                                            ) : (unit || <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>)}
                                        </td>
                                        <td style={centeredCell}>{planned > 0 ? planned.toLocaleString() : '-'}</td>
                                        <td style={centeredCell}>{actual > 0 ? actual.toLocaleString() : '-'}</td>
                                        <td style={{ ...centeredCell, color: variance > 0 ? '#d32f2f' : variance < 0 ? '#388e3c' : '#000' }}>
                                            {planned > 0 || actual > 0 ? variance.toLocaleString() : '-'}
                                        </td>
                                        <td style={centeredCell}>
                                            {planned > 0 ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                                    <div style={{ width: '60px', height: '12px', backgroundColor: '#e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${Math.min(parseFloat(pctComplete), 100)}%`, height: '100%', backgroundColor: parseFloat(pctComplete) >= 100 ? '#388e3c' : parseFloat(pctComplete) >= 75 ? '#2d6cdf' : '#f57c00', borderRadius: '6px' }} />
                                                    </div>
                                                    <span style={{ fontSize: '10px' }}>{pctComplete}%</span>
                                                </div>
                                            ) : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                        {(() => {
                            const overall = quantityTrackingData['__overall'] || {};
                            const oVariance = (overall.planned || 0) - (overall.actual || 0);
                            return (
                                <tr style={{ fontWeight: 'bold' }}>
                                    <td style={tdStyle}>Overall</td>
                                    <td style={centeredCell}>-</td>
                                    <td style={centeredCell}>{(overall.planned || 0) > 0 ? overall.planned.toLocaleString() : '-'}</td>
                                    <td style={centeredCell}>{(overall.actual || 0) > 0 ? overall.actual.toLocaleString() : '-'}</td>
                                    <td style={{ ...centeredCell, color: oVariance > 0 ? '#d32f2f' : oVariance < 0 ? '#388e3c' : '#000' }}>
                                        {(overall.planned || 0) > 0 || (overall.actual || 0) > 0 ? oVariance.toLocaleString() : '-'}
                                    </td>
                                    <td style={centeredCell}>
                                        {(overall.planned || 0) > 0 ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                                <div style={{ width: '60px', height: '12px', backgroundColor: '#e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${Math.min(parseFloat(overall.pctComplete || 0), 100)}%`, height: '100%', backgroundColor: parseFloat(overall.pctComplete || 0) >= 100 ? '#388e3c' : parseFloat(overall.pctComplete || 0) >= 75 ? '#2d6cdf' : '#f57c00', borderRadius: '6px' }} />
                                                </div>
                                                <span style={{ fontSize: '10px' }}>{overall.pctComplete || '0.0'}%</span>
                                            </div>
                                        ) : '-'}
                                    </td>
                                </tr>
                            );
                        })()}
                    </tbody>
                </table>
            </div>

            {/* QUALITY - Rework Hours Box */}
            <div>
                <div style={sectionHeaderStyle}>6&nbsp; Quality</div>
                <div style={{ border: '1px solid #999', backgroundColor: '#fff', padding: '10px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '8px', color: '#333' }}>Rework Hours</div>
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Discipline</th>
                                <th style={thStyle}>This Period</th>
                                <th style={thStyle}>Cumulative</th>
                                <th style={thStyle}>% of Total Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(progressData.length > 0 ? progressData : [{ discipline: 'Overall' }]).map((row) => {
                                const disc = row.discipline || 'Overall';
                                const rh = reworkHoursData[disc] || {};
                                return (
                                    <tr key={disc}>
                                        <td style={tdStyle}>{disc}</td>
                                        <td style={centeredCell} onClick={() => setEditingReworkHoursCell(`${disc}_thisPeriod`)}>
                                            {editingReworkHoursCell === `${disc}_thisPeriod` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={rh.thisPeriod || '0'}
                                                    onBlur={(e) => {
                                                        handleReworkHoursChange(disc, 'thisPeriod', e.target.value);
                                                        setEditingReworkHoursCell(null);
                                                    }}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') { handleReworkHoursChange(disc, 'thisPeriod', e.target.value); setEditingReworkHoursCell(null); }
                                                    }}
                                                    autoFocus
                                                    style={{ width: '80px', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px', fontSize: '11px' }}
                                                />
                                            ) : (rh.thisPeriod || '0')}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingReworkHoursCell(`${disc}_cumulative`)}>
                                            {editingReworkHoursCell === `${disc}_cumulative` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={rh.cumulative || '0'}
                                                    onBlur={(e) => {
                                                        handleReworkHoursChange(disc, 'cumulative', e.target.value);
                                                        setEditingReworkHoursCell(null);
                                                    }}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') { handleReworkHoursChange(disc, 'cumulative', e.target.value); setEditingReworkHoursCell(null); }
                                                    }}
                                                    autoFocus
                                                    style={{ width: '80px', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px', fontSize: '11px' }}
                                                />
                                            ) : (rh.cumulative || '0')}
                                        </td>
                                        <td style={centeredCell} onClick={() => setEditingReworkHoursCell(`${disc}_pctTotal`)}>
                                            {editingReworkHoursCell === `${disc}_pctTotal` ? (
                                                <input
                                                    type="text"
                                                    defaultValue={rh.pctTotal || '0.0%'}
                                                    onBlur={(e) => {
                                                        handleReworkHoursChange(disc, 'pctTotal', e.target.value);
                                                        setEditingReworkHoursCell(null);
                                                    }}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') { handleReworkHoursChange(disc, 'pctTotal', e.target.value); setEditingReworkHoursCell(null); }
                                                    }}
                                                    autoFocus
                                                    style={{ width: '80px', textAlign: 'center', border: '1px solid #2d6cdf', padding: '4px', fontSize: '11px' }}
                                                />
                                            ) : (rh.pctTotal || '0.0%')}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ACCOMPLISHMENTS */}
            <div>
                <div style={sectionHeaderStyle}>7&nbsp; Accomplishments</div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Accomplishments past period</th>
                            <th style={thStyle}>Planned Activities for Next Period</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={tdStyle}>
                                {isEditingAccomplishments ? (
                                    <div>
                                        <textarea
                                            value={accomplishments}
                                            onChange={(e) => setAccomplishments(e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '100px',
                                                padding: '8px',
                                                fontSize: '11px',
                                                fontFamily: 'Arial, sans-serif',
                                                border: '1px solid #999',
                                                borderRadius: '4px',
                                                resize: 'vertical'
                                            }}
                                        />
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                            <button onClick={handleSaveAccomplishments} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                            <button onClick={() => setIsEditingAccomplishments(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div onClick={() => setIsEditingAccomplishments(true)} style={{ cursor: 'pointer', minHeight: '60px', whiteSpace: 'pre-line' }} title="Click to edit">
                                        {accomplishments || 'Click to add accomplishments...'}
                                    </div>
                                )}
                            </td>
                            <td style={tdStyle}>
                                {isEditingPlannedActivities ? (
                                    <div>
                                        <textarea
                                            value={plannedActivities}
                                            onChange={(e) => setPlannedActivities(e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '100px',
                                                padding: '8px',
                                                fontSize: '11px',
                                                fontFamily: 'Arial, sans-serif',
                                                border: '1px solid #999',
                                                borderRadius: '4px',
                                                resize: 'vertical'
                                            }}
                                        />
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                            <button onClick={handleSavePlannedActivities} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                            <button onClick={() => setIsEditingPlannedActivities(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div onClick={() => setIsEditingPlannedActivities(true)} style={{ cursor: 'pointer', minHeight: '60px', whiteSpace: 'pre-line' }} title="Click to edit">
                                        {plannedActivities || 'Click to add planned activities...'}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={{ ...sectionHeaderStyle, borderTop: "none" }}>
                    Opportunities for Improvements
                </div>
                <div style={{ padding: "4px 6px" }}>
                    {isEditingOpportunities ? (
                        <div>
                            <textarea
                                value={opportunities}
                                onChange={(e) => setOpportunities(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: '100px',
                                    padding: '8px',
                                    fontSize: '11px',
                                    fontFamily: 'Arial, sans-serif',
                                    border: '1px solid #999',
                                    borderRadius: '4px',
                                    resize: 'vertical'
                                }}
                            />
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                <button onClick={handleSaveOpportunities} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                <button onClick={() => setIsEditingOpportunities(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div onClick={() => setIsEditingOpportunities(true)} style={{ cursor: 'pointer', minHeight: '60px', whiteSpace: 'pre-line' }} title="Click to edit">
                            {opportunities || 'Click to add opportunities for improvement...'}
                        </div>
                    )}
                </div>
            </div>

            {/* BUDGET OVERVIEW & INDEXES */}
            <div>
                <div style={{ ...sectionHeaderStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>8&nbsp; Budget Overview & Indexes</span>
                    {/* <button
                        onClick={exportBudgetToExcel}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#2d6cdf',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            marginLeft: '8px'
                        }}
                    >
                        Export to Excel
                    </button> */}
                </div>
                <table style={tableStyle}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                            <th style={thStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span>Job Cost</span>
                                    <button
                                        onClick={() => setCollapsedDisciplines(prev => {
                                            const allDisciplines = budgetRows
                                                .filter(row => row.level === 0 && row.discipline)
                                                .map(row => row.discipline);
                                            const allCollapsed = allDisciplines.every(d => prev[d]);
                                            const newState = {};
                                            allDisciplines.forEach(d => {
                                                newState[d] = !allCollapsed;
                                            });
                                            return newState;
                                        })}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0',
                                            fontSize: '12px',
                                            color: '#2d6cdf',
                                            fontWeight: 'bold',
                                            marginLeft: '4px'
                                        }}
                                        title="Collapse/Expand all disciplines"
                                    >
                                        ▼
                                    </button>
                                </div>
                            </th>
                            {budgetColumns.map((col) => (
                                <th key={col} style={thStyle}>
                                    {col === 'ETC Total' ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                            <span>{col}</span>
                                            <button
                                                onClick={() => setEtcBreakdownExpanded(!etcBreakdownExpanded)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    fontSize: '10px',
                                                    color: '#000',
                                                    fontWeight: 'bold'
                                                }}
                                                title={etcBreakdownExpanded ? 'Collapse ETC breakdown' : 'Expand ETC breakdown'}
                                            >
                                                {etcBreakdownExpanded ? '▼' : '▶'}
                                            </button>
                                        </div>
                                    ) : col}
                                </th>
                            ))}
                            <th style={thStyle}>PF</th>
                            <th style={thStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                    <span>CPI</span>
                                    <button
                                        onClick={() => setSpiExpanded(!spiExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0',
                                            fontSize: '10px',
                                            color: '#2d6cdf',
                                            fontWeight: 'bold'
                                        }}
                                        title={spiExpanded ? 'Hide SPI' : 'Show SPI'}
                                    >
                                        {spiExpanded ? '▼' : '▶'}
                                    </button>
                                </div>
                            </th>
                            {spiExpanded && <th style={thStyle}>SPI</th>}
                            <th style={{ ...thStyle, minWidth: '200px' }} className="no-print">Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {budgetRows.map((row, rowIndex) => {
                            // Get the data key for this row
                            const getDataKey = (row) => {
                                // Special categories that always exist
                                if (row.label === 'Contract Amount') return 'Contract Amount';
                                if (row.label === 'Total Costs') return 'Total Costs';
                                if (row.label === 'Contingency') return 'Contingency';

                                // For cost code sub-rows (level 1)
                                if (row.costCode) {
                                    return `costCode_${row.costCode}`;
                                }

                                // For discipline rows (level 0), check if they exist in budgetData
                                if (row.discipline && budgetData[row.discipline]) {
                                    return row.discipline;
                                }

                                return null;
                            };

                            const dataKey = getDataKey(row);
                            const rowData = dataKey ? budgetData[dataKey] : null;

                            // Define background color and font weight for discipline rows (level 0)
                            const cellBgColor = row.level === 0 ? "#d0dae8" : "transparent";
                            const cellFontWeight = row.level === 0 ? "bold" : "normal";

                            // Hide cost code rows if their discipline is collapsed
                            if (row.level === 1 && row.discipline && collapsedDisciplines[row.discipline]) {
                                return null;
                            }

                            return (
                                <tr key={row.label} className={row.level === 1 ? 'cost-code-row' : ''}>
                                    <td
                                        style={{
                                            ...tdStyle,
                                            paddingLeft: 4 + row.level * 14,
                                            fontWeight: row.level === 0 ? "bold" : "normal",
                                            backgroundColor: cellBgColor,
                                        }}
                                    >
                                        {row.level === 1 && row.costCode ? (
                                            <>
                                                {editingDisciplineTitle === row.costCode ? (
                                                    <input
                                                        type="text"
                                                        value={disciplineTitles[row.costCode] || row.label}
                                                        onChange={(e) => setDisciplineTitles(prev => ({ ...prev, [row.costCode]: e.target.value }))}
                                                        onBlur={() => handleSaveDisciplineTitle(row.costCode, disciplineTitles[row.costCode] || row.label)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleSaveDisciplineTitle(row.costCode, disciplineTitles[row.costCode] || row.label);
                                                            }
                                                        }}
                                                        autoFocus
                                                        style={{
                                                            border: '1px solid #2d6cdf',
                                                            padding: '2px 4px',
                                                            fontSize: '11px',
                                                            fontWeight: 'normal',
                                                            minWidth: '100px'
                                                        }}
                                                    />
                                                ) : (
                                                    <span 
                                                        onClick={() => setEditingDisciplineTitle(row.costCode)}
                                                        style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                                        title="Click to edit title"
                                                    >
                                                        {disciplineTitles[row.costCode] || row.label}
                                                    </span>
                                                )}
                                            </>
                                        ) : row.level === 0 && row.discipline ? (
                                            <>
                                                {editingDisciplineTitle === row.discipline ? (
                                                    <input
                                                        type="text"
                                                        value={disciplineTitles[row.discipline] || row.discipline}
                                                        onChange={(e) => setDisciplineTitles(prev => ({ ...prev, [row.discipline]: e.target.value }))}
                                                        onBlur={() => handleSaveDisciplineTitle(row.discipline, disciplineTitles[row.discipline] || row.discipline)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleSaveDisciplineTitle(row.discipline, disciplineTitles[row.discipline] || row.discipline);
                                                            }
                                                        }}
                                                        autoFocus
                                                        style={{
                                                            border: '1px solid #2d6cdf',
                                                            padding: '2px 4px',
                                                            fontSize: '11px',
                                                            fontWeight: 'bold',
                                                            minWidth: '100px'
                                                        }}
                                                    />
                                                ) : (
                                                    <span 
                                                        onClick={() => setEditingDisciplineTitle(row.discipline)}
                                                        style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                                        title="Click to edit title"
                                                    >
                                                        {disciplineTitles[row.discipline] || row.discipline}
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            row.label
                                        )}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.k2Budget || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.bidBudget || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.approvedCOs || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.currentBudget || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.actualCosts || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.openCommit || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.totalCommit || '-'}</td>
                                    {etcBreakdownExpanded && (
                                        <>
                                            <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etcLabor || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etcEquipment || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etcMaterials || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etcRentals || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etcSubs || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etcOther || '-'}</td>
                                        </>
                                    )}
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.etc || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.eac || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.deltaEACBudget || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}>{rowData?.deltaEACPrevious || '-'}</td>
                                    {/* <td style={{ ...tdStyle, textAlign: 'right' }}>{rowData?.planPerQty || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{rowData?.actualPerQty || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{rowData?.trendPerQty || '-'}</td> */}
                                    <td 
                                        style={{ ...tdStyle, textAlign: 'right', cursor: 'pointer', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}
                                        onClick={() => dataKey && handlePfEdit(dataKey, rowData?.pf)}
                                    >
                                        {editingPfKey === dataKey ? (
                                            <input
                                                type="text"
                                                value={editedPfValues[dataKey] || ''}
                                                onChange={(e) => handlePfChange(dataKey, e.target.value)}
                                                onBlur={() => handlePfSave(dataKey)}
                                                onKeyPress={(e) => e.key === 'Enter' && handlePfSave(dataKey)}
                                                autoFocus
                                                style={{
                                                    width: '100%',
                                                    textAlign: 'right',
                                                    border: '1px solid #2d6cdf',
                                                    padding: '2px',
                                                    fontSize: '11px'
                                                }}
                                            />
                                        ) : (
                                            rowData?.pf || '-'
                                        )}
                                    </td>
                                    <td 
                                        style={{ ...tdStyle, textAlign: 'right', cursor: 'pointer', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}
                                        onClick={() => dataKey && handleCpiEdit(dataKey, rowData?.cpi)}
                                    >
                                        {editingCpiKey === dataKey ? (
                                            <input
                                                type="text"
                                                value={editedCpiValues[dataKey] || ''}
                                                onChange={(e) => handleCpiChange(dataKey, e.target.value)}
                                                onBlur={() => handleCpiSave(dataKey)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleCpiSave(dataKey)}
                                                autoFocus
                                                style={{
                                                    width: '100%',
                                                    textAlign: 'right',
                                                    border: '1px solid #2d6cdf',
                                                    padding: '2px',
                                                    fontSize: '11px'
                                                }}
                                            />
                                        ) : (
                                            rowData?.cpi || '-'
                                        )}
                                    </td>
                                    {spiExpanded && (
                                        <td 
                                            style={{ ...tdStyle, textAlign: 'right', cursor: 'pointer', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}
                                            onClick={() => dataKey && handleSpiEdit(dataKey, rowData?.spi)}
                                        >
                                            {editingSpiKey === dataKey ? (
                                                <input
                                                    type="text"
                                                    value={editedSpiValues[dataKey] || ''}
                                                    onChange={(e) => handleSpiChange(dataKey, e.target.value)}
                                                    onBlur={() => handleSpiSave(dataKey)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSpiSave(dataKey)}
                                                    autoFocus
                                                    style={{
                                                        width: '100%',
                                                        textAlign: 'right',
                                                        border: '1px solid #2d6cdf',
                                                        padding: '2px',
                                                        fontSize: '11px'
                                                    }}
                                                />
                                            ) : (
                                                rowData?.spi || '-'
                                            )}
                                        </td>
                                    )}
                                    <td 
                                        style={{ ...tdStyle, cursor: 'pointer', minWidth: '200px', backgroundColor: cellBgColor, fontWeight: cellFontWeight }}
                                        onClick={() => dataKey && setEditingBudgetNote(dataKey)}
                                        className="no-print"
                                    >
                                        {editingBudgetNote === dataKey ? (
                                            <textarea
                                                value={budgetNotes[dataKey] || ''}
                                                onChange={(e) => setBudgetNotes(prev => ({ ...prev, [dataKey]: e.target.value }))}
                                                onBlur={() => handleSaveBudgetNote(dataKey, budgetNotes[dataKey] || '')}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.ctrlKey) {
                                                        handleSaveBudgetNote(dataKey, budgetNotes[dataKey] || '');
                                                    } else if (e.key === 'Escape') {
                                                        setEditingBudgetNote(null);
                                                    }
                                                }}
                                                autoFocus
                                                style={{
                                                    width: '100%',
                                                    minHeight: '40px',
                                                    border: '1px solid #2d6cdf',
                                                    padding: '4px',
                                                    fontSize: '11px',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        ) : (
                                            <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                                {budgetNotes[dataKey] || '-'}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Job Profit & JP% rows */}
                <table style={{ ...tableStyle, marginTop: '2px' }}>
                    <tbody>
                        <tr style={{ fontWeight: 'bold', backgroundColor: '#e8f0fe' }}>
                            <td style={{ ...tdStyle, fontWeight: 'bold' }}>Job Profit</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpBudget || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpBudget || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpActual || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            {etcBreakdownExpanded && <>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            </>}
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpEAC || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            {spiExpanded && <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>}
                            <td style={{ ...tdStyle }} className="no-print">-</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', backgroundColor: '#e8f0fe' }}>
                            <td style={{ ...tdStyle, fontWeight: 'bold' }}>JP %</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpPctBudget || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpPctBudget || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpPctActual || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            {etcBreakdownExpanded && <>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            </>}
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{jobProfitData?.jpPctEAC || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                            {spiExpanded && <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>}
                            <td style={{ ...tdStyle }} className="no-print">-</td>
                        </tr>
                    </tbody>
                </table>

                {/* Change Orders Summary */}
                <div style={sectionHeaderStyle}>Change Orders</div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Approved</th>
                            <th style={thStyle}>Pending Performed</th>
                            <th style={thStyle}>Pending Not Performed</th>
                            <th style={thStyle}>Not Submitted</th>
                            <th style={thStyle}>Denied</th>
                            <th style={{ ...thStyle, minWidth: '200px' }}>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ ...centeredCell, fontWeight: 'bold' }}>{fNum(changeOrderSummary.approved, 1)}</td>
                            <td style={centeredCell}>{fNum(changeOrderSummary.pendingPerformed, 1)}</td>
                            <td style={centeredCell}>{fNum(changeOrderSummary.pendingNotPerformed, 1)}</td>
                            <td style={centeredCell}>{fNum(changeOrderSummary.notSubmitted, 1)}</td>
                            <td style={centeredCell}>{fNum(changeOrderSummary.denied, 1)}</td>
                            <td style={tdStyle}>
                                {isEditingChangeOrderNotes ? (
                                    <div>
                                        <textarea
                                            value={changeOrderNotes}
                                            onChange={(e) => setChangeOrderNotes(e.target.value)}
                                            style={{ width: '100%', minHeight: '50px', padding: '4px', fontSize: '11px', fontFamily: 'inherit', border: '1px solid #999', borderRadius: '4px', resize: 'vertical' }}
                                        />
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                            <button onClick={handleSaveChangeOrderNotes} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                            <button onClick={() => setIsEditingChangeOrderNotes(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div onClick={() => setIsEditingChangeOrderNotes(true)} style={{ cursor: 'pointer', minHeight: '30px', whiteSpace: 'pre-line', fontSize: '11px', color: changeOrderNotes ? '#000' : '#999', fontStyle: changeOrderNotes ? 'normal' : 'italic' }} title="Click to edit">
                                        {changeOrderNotes || 'Click to add notes...'}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Billing Summary */}
                <div style={sectionHeaderStyle}>Billing</div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Total Billed</th>
                            <th style={thStyle}>Over / Underbilled</th>
                            <th style={{ ...thStyle, minWidth: '200px' }}>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ ...centeredCell, fontWeight: 'bold' }}>
                                {budgetData?.['Contract Amount']?.actualCosts || '$0'}
                            </td>
                            <td style={{ ...centeredCell, fontWeight: 'bold', color: pNum(budgetData?.['Contract Amount']?.actualCosts || '0') - pNum(budgetData?.['Total Costs']?.actualCosts || '0') >= 0 ? '#2e7d32' : '#c62828' }}>
                                {fNum(pNum(budgetData?.['Contract Amount']?.actualCosts || '0') - pNum(budgetData?.['Total Costs']?.actualCosts || '0'), 1)}
                            </td>
                            <td style={tdStyle}>
                                {isEditingBillingNotes ? (
                                    <div>
                                        <textarea
                                            value={billingNotes}
                                            onChange={(e) => setBillingNotes(e.target.value)}
                                            style={{ width: '100%', minHeight: '50px', padding: '4px', fontSize: '11px', fontFamily: 'inherit', border: '1px solid #999', borderRadius: '4px', resize: 'vertical' }}
                                        />
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                            <button onClick={handleSaveBillingNotes} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                            <button onClick={() => setIsEditingBillingNotes(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div onClick={() => setIsEditingBillingNotes(true)} style={{ cursor: 'pointer', minHeight: '30px', whiteSpace: 'pre-line', fontSize: '11px', color: billingNotes ? '#000' : '#999', fontStyle: billingNotes ? 'normal' : 'italic' }} title="Click to edit">
                                        {billingNotes || 'Click to add billing notes...'}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={sectionHeaderStyle}>9&nbsp; Cash Out / Variances, Manpower & Trends</div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: "40%" }}>Cash Out / Variances With Previous Forecast</th>
                            <th style={{ ...thStyle, width: "35%" }}>Manpower (Weekly Hours)</th>
                            <th style={{ ...thStyle, width: "25%" }}>Trends / Cash</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ ...tdStyle, width: "40%" }}>
                                {isEditingCashOutVariances ? (
                                    <div style={{ marginTop: '8px' }}>
                                        <textarea
                                            value={cashOutVariances}
                                            onChange={(e) => setCashOutVariances(e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '80px',
                                                padding: '8px',
                                                fontSize: '11px',
                                                fontFamily: 'Arial, sans-serif',
                                                border: '1px solid #999',
                                                borderRadius: '4px',
                                                resize: 'vertical'
                                            }}
                                        />
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                            <button onClick={handleSaveCashOutVariances} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                            <button onClick={() => setIsEditingCashOutVariances(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div 
                                        onClick={() => setIsEditingCashOutVariances(true)} 
                                        style={{ cursor: 'pointer', minHeight: '60px', whiteSpace: 'pre-line', marginTop: '8px' }} 
                                        title="Click to edit"
                                    >
                                        {cashOutVariances || 'Click to add variance notes...'}
                                    </div>
                                )}
                            </td>
                            <td style={{ ...tdStyle, width: "35%", verticalAlign: 'bottom' }}>
                                {/* Manpower Bar Chart */}
                                {manpowerChartData.length > 0 ? (
                                    <div style={{ padding: '4px' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px', borderBottom: '1px solid #999', paddingBottom: '2px' }}>
                                            {manpowerChartData.map((item, idx) => {
                                                const maxVal = Math.max(...manpowerChartData.map(d => Math.max(d.planned, d.actual)), 1);
                                                const plannedHeight = (item.planned / maxVal) * 100;
                                                const actualHeight = (item.actual / maxVal) * 100;
                                                return (
                                                    <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                                                        <div style={{ display: 'flex', gap: '1px', alignItems: 'flex-end', height: '100%' }}>
                                                            <div style={{ width: '8px', height: `${plannedHeight}%`, backgroundColor: '#A5A5A5', borderRadius: '1px 1px 0 0', minHeight: item.planned > 0 ? '2px' : '0' }} title={`Planned: ${item.planned}`} />
                                                            <div style={{ width: '8px', height: `${actualHeight}%`, backgroundColor: '#F4B183', borderRadius: '1px 1px 0 0', minHeight: item.actual > 0 ? '2px' : '0' }} title={`Actual: ${item.actual}`} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div style={{ display: 'flex', gap: '2px' }}>
                                            {manpowerChartData.map((item, idx) => (
                                                <div key={idx} style={{ flex: 1, textAlign: 'center', fontSize: '8px', color: '#666', marginTop: '2px' }}>{item.week}</div>
                                            ))}
                                        </div>
                                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '4px', fontSize: '9px' }}>
                                            <span><span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#A5A5A5', marginRight: '3px', borderRadius: '1px' }} />Planned</span>
                                            <span><span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#F4B183', marginRight: '3px', borderRadius: '1px' }} />Actual</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', color: '#999', fontStyle: 'italic', padding: '20px', fontSize: '11px' }}>
                                        No manpower data available.<br/>Enter hours in the Labor Graphs tab.
                                    </div>
                                )}
                            </td>
                            <td style={{ ...tdStyle, width: "25%" }}>
                                {isEditingTrendsCash ? (
                                    <div style={{ marginTop: '8px' }}>
                                        <textarea
                                            value={trendsCash}
                                            onChange={(e) => setTrendsCash(e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '80px',
                                                padding: '8px',
                                                fontSize: '11px',
                                                fontFamily: 'Arial, sans-serif',
                                                border: '1px solid #999',
                                                borderRadius: '4px',
                                                resize: 'vertical'
                                            }}
                                        />
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                            <button onClick={handleSaveTrendsCash} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                            <button onClick={() => setIsEditingTrendsCash(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div 
                                        onClick={() => setIsEditingTrendsCash(true)} 
                                        style={{ cursor: 'pointer', minHeight: '60px', whiteSpace: 'pre-line', marginTop: '8px' }} 
                                        title="Click to edit"
                                    >
                                        {trendsCash || 'Click to add trends/cash notes...'}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* TOP RISKS */}
            <div>
                <div style={sectionHeaderStyle}>
                    10&nbsp; Top Risks
                    <button 
                        onClick={handleAddRisk} 
                        style={{ 
                            marginLeft: '10px', 
                            padding: '4px 8px', 
                            backgroundColor: '#2d6cdf', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '10px',
                            fontWeight: 'bold'
                        }}
                    >
                        + Add Risk
                    </button>
                </div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Risk</th>
                            <th style={thStyle}>Description</th>
                            <th style={thStyle}>Action taken</th>
                            <th style={thStyle}>Impact</th>
                            <th style={thStyle}>Prob. of Occurrence</th>
                            <th style={{ ...thStyle, width: '80px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {risks.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ ...tdStyle, textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                                    No risks added. Click "+ Add Risk" to add one.
                                </td>
                            </tr>
                        ) : (
                            risks.map(risk => (
                                editingRiskId === risk.id ? (
                                    <tr key={risk.id}>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingRisk.risk}
                                                onChange={(e) => setEditingRisk({ ...editingRisk, risk: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingRisk.description}
                                                onChange={(e) => setEditingRisk({ ...editingRisk, description: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <textarea
                                                value={editingRisk.actionTaken}
                                                onChange={(e) => setEditingRisk({ ...editingRisk, actionTaken: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999', minHeight: '40px' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingRisk.impact}
                                                onChange={(e) => setEditingRisk({ ...editingRisk, impact: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            />
                                        </td>
                                        <td style={centeredCell}>
                                            <input
                                                type="text"
                                                value={editingRisk.probability}
                                                onChange={(e) => setEditingRisk({ ...editingRisk, probability: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999', textAlign: 'center' }}
                                            />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <button onClick={handleSaveRisk} style={{ padding: '4px 8px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', marginRight: '4px' }}>Save</button>
                                            <button onClick={handleCancelRisk} style={{ padding: '4px 8px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>Cancel</button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={risk.id} onClick={() => handleEditRisk(risk)} style={{ cursor: 'pointer' }} title="Click to edit">
                                        <td style={tdStyle}>{risk.risk || '-'}</td>
                                        <td style={tdStyle}>{risk.description || '-'}</td>
                                        <td style={tdStyle}>{risk.actionTaken || '-'}</td>
                                        <td style={tdStyle}>{risk.impact || '-'}</td>
                                        <td style={centeredCell}>{risk.probability || '-'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDeleteRisk(risk.id); }}
                                                title="Delete"
                                                style={{ padding: '4px 8px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '3px', cursor: 'pointer', fontSize: '14px' }}
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                )
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* TOP ISSUES */}
            <div>
                <div style={sectionHeaderStyle}>
                    11&nbsp; Top Issues
                    <button 
                        onClick={handleAddIssue} 
                        style={{ 
                            marginLeft: '10px', 
                            padding: '4px 8px', 
                            backgroundColor: '#2d6cdf', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '10px',
                            fontWeight: 'bold'
                        }}
                    >
                        + Add Issue
                    </button>
                </div>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Issue</th>
                            <th style={thStyle}>Description</th>
                            <th style={thStyle}>Action Taken</th>
                            <th style={thStyle}>Priority</th>
                            <th style={thStyle}>Date for Resolution</th>
                            <th style={{ ...thStyle, width: '80px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {issues.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ ...tdStyle, textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                                    No issues added. Click "+ Add Issue" to add one.
                                </td>
                            </tr>
                        ) : (
                            issues.map(issue => (
                                editingIssueId === issue.id ? (
                                    <tr key={issue.id}>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingIssue.issue}
                                                onChange={(e) => setEditingIssue({ ...editingIssue, issue: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="text"
                                                value={editingIssue.description}
                                                onChange={(e) => setEditingIssue({ ...editingIssue, description: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <textarea
                                                value={editingIssue.actionTaken}
                                                onChange={(e) => setEditingIssue({ ...editingIssue, actionTaken: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999', minHeight: '40px' }}
                                            />
                                        </td>
                                        <td style={centeredCell}>
                                            <select
                                                value={editingIssue.priority}
                                                onChange={(e) => setEditingIssue({ ...editingIssue, priority: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            >
                                                <option value="">Select</option>
                                                <option value="Low">Low</option>
                                                <option value="Medium">Medium</option>
                                                <option value="High">High</option>
                                                <option value="Critical">Critical</option>
                                            </select>
                                        </td>
                                        <td style={centeredCell}>
                                            <input
                                                type="date"
                                                value={editingIssue.dateForResolution}
                                                onChange={(e) => setEditingIssue({ ...editingIssue, dateForResolution: e.target.value })}
                                                style={{ width: '100%', padding: '4px', fontSize: '11px', border: '1px solid #999' }}
                                            />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <button onClick={handleSaveIssue} style={{ padding: '4px 8px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', marginRight: '4px' }}>Save</button>
                                            <button onClick={handleCancelIssue} style={{ padding: '4px 8px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>Cancel</button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={issue.id} onClick={() => handleEditIssue(issue)} style={{ cursor: 'pointer' }} title="Click to edit">
                                        <td style={tdStyle}>{issue.issue || '-'}</td>
                                        <td style={tdStyle}>{issue.description || '-'}</td>
                                        <td style={tdStyle}>{issue.actionTaken || '-'}</td>
                                        <td style={centeredCell}>{issue.priority || '-'}</td>
                                        <td style={centeredCell}>{issue.dateForResolution ? moment(issue.dateForResolution).format('M/D/YYYY') : '-'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDeleteIssue(issue.id); }}
                                                title="Delete"
                                                style={{ padding: '4px 8px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '3px', cursor: 'pointer', fontSize: '14px' }}
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                )
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ADDITIONAL COMMENTS */}
            <div>
                <div style={sectionHeaderStyle}>
                    12&nbsp; Additional Comments and Observations
                </div>
                <div style={{ padding: "12px 6px", minHeight: 60 }}>
                    {isEditingAdditionalComments ? (
                        <div>
                            <textarea
                                value={additionalComments}
                                onChange={(e) => setAdditionalComments(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: '100px',
                                    padding: '8px',
                                    fontSize: '11px',
                                    fontFamily: 'Arial, sans-serif',
                                    border: '1px solid #999',
                                    borderRadius: '4px',
                                    resize: 'vertical'
                                }}
                            />
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                <button onClick={handleSaveAdditionalComments} style={{ padding: '6px 12px', backgroundColor: '#2d6cdf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Save</button>
                                <button onClick={() => setIsEditingAdditionalComments(false)} style={{ padding: '6px 12px', backgroundColor: '#f2f2f2', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div onClick={() => setIsEditingAdditionalComments(true)} style={{ cursor: 'pointer', minHeight: '60px', whiteSpace: 'pre-line' }} title="Click to edit">
                            {additionalComments || 'Click to add additional comments and observations...'}
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}

export default Overview;

